import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  ResponseType,
} from "axios";
import { DnsRecord } from "entrijs";

export interface FullRequestParams
  extends Omit<AxiosRequestConfig, "data" | "params" | "url" | "responseType"> {
  secure?: boolean;
  path: string;
  type?: ContentType;
  query?: QueryParamsType;
  format?: ResponseType;
  body?: unknown;
}

export type RequestParams = Omit<
  FullRequestParams,
  "body" | "method" | "query" | "path"
>;

export interface ApiConfig<SecurityDataType = unknown>
  extends Omit<AxiosRequestConfig, "data" | "cancelToken"> {
  securityWorker?: (
    securityData: SecurityDataType | null
  ) => Promise<AxiosRequestConfig | void> | AxiosRequestConfig | void;
  secure?: boolean;
  format?: ResponseType;
}

export enum ContentType {
  Json = "application/json",
  FormData = "multipart/form-data",
  UrlEncoded = "application/x-www-form-urlencoded",
}

export type QueryParamsType = Record<string | number, any>;

export interface LeadIdentityView {
  id?: string;
}

export interface SenderDetailsUpdateView {
  name?: string;

  email?: string;

  address?: string;

  city?: string;

  region?: string;

  country?: string;

  postalCode?: string;

  defaults?: boolean;

  sendConfirmationEmail?: boolean;
}

export interface CreateSuppressionRequestView {
  name: string;
  type: string;
  scope: string;
}

export interface suppressionDetailsItem {
  value: string;
  description: string;
}

export interface CreateSuppressionResponseView {
  id?: string;
  name: string;
  type: string;
  scope: string;
}

export interface UploadFileView {
  id?: number;
  fileUrl?: string;
  name?: string;
  scope?: string;
  type?: string;
}

export interface SuppressionFileView {
  fileName?: string;
  url?: string;
  createdAt?: number;
}

export interface SuppressionFilesView {
  files: SuppressionFileView[];
}

export interface SuppressionZipView {
  url?: string;
}

export interface SenderDetailsAuthenticationView {
  dkimSigned: boolean;
  dmarcSigned: boolean;
  spfSigned: boolean;
  bounceRouteConfigured: boolean;
  dkimValid?: boolean;
  dmarcValid?: boolean;
  spfValid?: boolean;
  dkimErrors?: string[];
  dmarcErrors?: string[];
  spfErrors?: string[];
}

export interface ResolveIssueView {
  accountId: string;
  accountIssueType: string;
}

export interface SenderDetailsView {
  id?: string;

  name?: string;

  email?: string;

  address?: string;

  city?: string;

  region?: string;

  country?: string;

  postalCode?: string;

  emailConfirmed?: boolean;

  dkimSigned?: boolean;

  dmarcSigned?: boolean;

  spfSigned?: boolean;

  bounceRouteConfigured?: boolean;

  defaults?: boolean;

  assignedToCampaigns?: boolean;

  dkimValid?: boolean;

  dmarcValid?: boolean;

  spfValid?: boolean;

  dkimErrors?: string[];

  dmarcErrors?: string[];

  spfErrors?: string[];
}

export interface CampaignContentSourceRequestView {
  contentSourceUrl: string;
}

export interface SenderDetailsFilterView {
  searchTextName?: string;
}

export interface PageSenderDetailsView {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  numberOfElements?: number;

  pageable?: Pageable;

  first?: boolean;

  last?: boolean;

  size?: number;

  content?: SenderDetailsView[];

  number?: number;

  empty?: boolean;
}
export interface UserDetailsFilterView {
  searchTextName?: string;
  statuses?: string[];
  roles?: string[];
}

export interface PageUserDetailsView {
  totalPages?: number;
  totalElements?: number;
  sort?: Sort;
  numberOfElements?: number;
  pageable?: Pageable;
  first?: boolean;
  last?: boolean;
  size?: number;
  content?: UserDetailsView[];
  number?: number;
  empty?: boolean;
}

/**
 * Roles an account member can hold. A member holds a SET of these, not one:
 * Admin + Billing Contact on the same user is an expected combination.
 *
 * SUPER_ADMIN appears in responses only — it marks the account owner, is
 * granted once at account creation, and the API rejects it in every create and
 * update with a 412. See AssignableAccountRole.
 */
export type AccountRole = "SUPER_ADMIN" | "ADMIN" | "BILLING" | "MEMBER";

/** The roles a client may actually send. SUPER_ADMIN is never assignable. */
export type AssignableAccountRole = Exclude<AccountRole, "SUPER_ADMIN">;

/**
 * Effective permissions. Always branch on these, never on a role name — the
 * role -> permission mapping lives on the server and changes independently.
 *
 * `MANAGE_BILLING` was removed: it was too coarse to express "Admin can top up
 * but cannot see invoices", which the split below does.
 */
export type AccountPermission =
  | "ACCESS_APP"
  | "ACCESS_CAMPAIGNS"
  | "ASSIGN_BILLING_CONTACT"
  | "MANAGE_PLAN"
  | "VIEW_INVOICES"
  | "MANAGE_BILLING_DETAILS"
  | "TOP_UP_CREDITS"
  | "MANAGE_MEMBERS"
  | "MANAGE_API_KEYS"
  | "MANAGE_SUB_ACCOUNTS";

/** GET /account-member/me/permissions — the caller's own roles/permissions. */
export interface AccountMemberPermissionsView {
  roles?: AccountRole[];
  permissions?: AccountPermission[];
}

export interface UserDetailsView {
  id: string;
  userId: string;
  accountId: string;
  email: string;
  /**
   * @deprecated Legacy derived column kept for old server-side lookups. It is
   * computed from `roles` and SUPER_ADMIN is reported here as "ADMIN", so
   * rendering this labels the account owner "Admin" and makes the Super Admin
   * role look absent from the product. Read `roles` instead — see
   * getMemberRoles() in ./authz.
   */
  role: "ADMIN" | "MEMBER" | "BILLING";
  /**
   * The member's full role set. The only field safe to render or branch on.
   * The owner carries ["SUPER_ADMIN"] alone — it does not also list ADMIN,
   * because Super Admin already grants everything Admin does.
   */
  roles?: AccountRole[];
  status:
    | "ACCEPTED"
    | "ACTIVE"
    | "CANCELED"
    | "DELETED"
    | "EXPIRED"
    | "INACTIVE"
    | "INVITED";
  invitedAt: string;
  acceptedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface Pageable {
  page?: number;

  size?: number;

  sort?: string[];
}

export interface Sort {
  sorted?: boolean;

  unsorted?: boolean;

  empty?: boolean;
}

export interface ExportInvoicesView {
  invoiceIds?: string[];
}

export interface IntegrationFilterView {
  name?: string;

  systemNames?: string[];

  integrationType?:
    | "ALL"
    | "CRM"
    | "E_COMMERCE"
    | "EMAIL_PROVIDER"
    | "PAYMENTS"
    | "FORMS"
    | "OTHERS";
}

export interface SetupMemberPassword {
  password: string;
  confirmationPassword: string;
}

export interface IntegrationItemView {
  id?: string;

  name?: string;

  systemName?: string;

  siteLink?: string;

  logoDefault?: string;

  logoChecked?: string;

  description?: string;

  integrationTypes?: (
    | "ALL"
    | "CRM"
    | "E_COMMERCE"
    | "EMAIL_PROVIDER"
    | "PAYMENTS"
    | "FORMS"
    | "OTHERS"
  )[];

  connectionType?: "OAUTH" | "ACCESS_KEY" | "CUSTOM" | "REDIRECT";

  parameters?: Record<string, string>;

  priority?: number;

  guideLink?: string;
}

export interface IntegrationsView {
  integrations?: IntegrationItemView[];
}

export interface IdentityLogoView {
  identityId?: string;

  logoUrl?: string;
}

export interface IdentityView {
  id?: string;

  logoUrl?: string;

  backgroundColor?: string;

  linksColor?: string;

  bodyTextColor?: string;

  buttonsColor?: string;

  topMenuColor?: string;

  titleFont?: string;

  paragraphFont?: string;

  socialLinks?: SocialLinkView[];

  links?: LinkView[];
}

export interface LinkView {
  id?: string;

  text?: string;

  url?: string;
}

export interface SocialLinkView {
  id?: string;

  type?: string;

  url?: string;
}

export interface MfaEmailOtpRequestView {
  accountMemberId: string;
  type: "EMAIL_OTP" | "TOTP";
}

export interface MfaPreferenceRequestView {
  mfaType: "EMAIL_OTP" | "TOTP";
}

export type CampaignSendOrder = "ALPHABETICAL" | "RANDOM" | "ENGAGEMENT_SCORE";

export interface CampaignUpdateRequestView {
  name?: string;

  subject?: string;

  preview?: string;

  smartAssistant?: boolean;

  sendOrder?: CampaignSendOrder;
}

export interface PredefinedSegmentsCountsResponseView {
  numberOfCustomers: number;
}

export interface customFontsResponseView {
  id: string;
  fontName: string;
  fontFamily: string;
  fontUrl: string;
  createdAt: string;
  updatedAt: string;
}

export interface customFontsRequestView {
  fontName: string;
  fontFamily: string;
  fontUrl: string;
}
/**
 * Response of `POST /campaign/{id}/clone-undelivered` (MAR-12654). The backend returns the full campaign view for the
 * newly created draft; only the fields the UI actually consumes are declared here, rather than mirroring all of it.
 * Widen this as more fields are needed.
 */
export interface CampaignCloneUndeliveredResponseView {
  id?: string;

  name?: string;
}

export interface CampaignResponseView {
  id?: string;

  senderDetailsId?: string;

  senderEmail?: string;

  senderName?: string;

  name?: string;

  subject?: string;

  preview?: string;

  smartAssistant?: boolean;

  start?: number;

  completed?: number;

  campaignStatus?:
    | "DRAFT"
    | "LAUNCHED"
    | "QUEUED"
    | "COMPLETED"
    | "SCHEDULED"
    | "PAUSED"
    | "DELETED"
    | "FAILED"
    | "RECURRING"
    | "STOPPED";

  template?: boolean;

  parentTemplateId?: string;

  requests?: number;

  sent?: number;

  open?: number;

  totalOpen?: number;

  clicked?: number;

  totalClicked?: number;

  unsubscribed?: number;

  delivered?: number;

  bounced?: number;

  spam?: number;

  total?: number;

  sentProgress?: number;

  openProgress?: number;

  totalOpenProgress?: number;

  clickedProgress?: number;

  totalClickedProgress?: number;

  unsubscribedProgress?: number;

  deliveredProgress?: number;

  bouncedProgress?: number;

  spamProgress?: number;

  totalProgress?: number;

  remainedTestEmails?: number;

  autoTrackingEnabled?: boolean;

  contentSourceUrl?: string;

  time?: string;

  days?: string[];

  suppressions?: SuppressionView[];

  /**
   * Contacts skipped because they matched a GLOBAL or LOCAL suppression list.
   * Nullable: rows written before the field was mapped load as SQL NULL.
   */
  suppressed?: number | null;

  /**
   * Recipients dropped because they belonged to one of the campaign's excluded segments.
   * Nullable: null means the count could not be established, which is not the same as zero.
   */
  excludedBySegment?: number | null;

  /**
   * Recipients dropped because they were active in one of the campaign's excluded workflows.
   * Nullable: null means the count could not be established, which is not the same as zero.
   */
  excludedByWorkflow?: number | null;

  /**
   * Recipient ordering for the Fetch And Send Traffic Shaping path. Null/unset reads as
   * ALPHABETICAL, preserving today's default order for existing campaigns.
   */
  sendOrder?: CampaignSendOrder;
}

export interface CampaignChildLastSentResponseView {
  id?: string;

  accountId?: string;

  name?: string;

  enabled?: boolean;

  startTime?: number;

  completedTime?: number;

  template?: any;

  start?: number;

  completed?: number;
}

export interface CampaignTestEmailsView {
  testEmails: string[];

  activityId?: string;
}

export interface CampaignRemainedTestEmailsView {
  remainedTestEmails?: number;

  remainedTestEmailsPerDay?: number;
}

export interface CampaignStartView {
  start?: number;

  isScheduled?: boolean;

  days?: string[];

  time?: string;
}

export interface SenderAllocationView {
  senderDetailsId?: string;

  percentage?: number;
}

export interface SenderAllocationsGetResponseView {
  allocations?: SenderAllocationView[];
}

export interface SenderDetailsIdentityView {
  senderDetailsId?: string;

  senderName?: string;

  senderEmail?: string;

  /** Multi-sender allocation; empty/absent keeps single-sender behavior. */
  allocations?: SenderAllocationView[];
}

export interface SegmentIdentitiesView {
  segments?: SegmentIdentityView[];
}

export interface AiTrafficShappingIdentitiesView {
  aiTrafficControls?: AITrafficShapingView[];
}

export interface SuppressionIdentitiesView {
  suppressions?: SuppressionView[];
}

export interface AudienceIdentitiesView {
  audiences?: SegmentIdentityView[];
}

/**
 * How a campaign excludes contacts that are mid-journey in a workflow.
 * `ANY_WORKFLOW` carries no ids; `SPECIFIC_WORKFLOWS` requires them.
 */
export type WorkflowExclusionScope = "ANY_WORKFLOW" | "SPECIFIC_WORKFLOWS";

/** Request body of `PUT /campaign/{id}/excluded-workflows`. A null scope clears the exclusion. */
export interface ExcludedWorkflowsView {
  scope?: WorkflowExclusionScope | null;

  workflowIds?: string[];
}

export interface WorkflowIdentityView {
  id?: string;

  name?: string;

  status?: string;
}

/** Response of `GET /campaign/{id}/excluded-workflows`. */
export interface ExcludedWorkflowsResponseView {
  scope?: WorkflowExclusionScope | null;

  workflows?: WorkflowIdentityView[];

  /** Selected workflows the workflow service no longer knows — they exclude nobody. */
  missingWorkflowIds?: string[];
}

export interface AudienceIdentityView {
  id?: string;

  name?: string;

  audienceType?: string;

  numberOfCustomers?: number;
}

export interface SegmentIdentityView {
  id?: string;

  name?: string;

  segmentType?: string;

  numberOfCustomers?: number;
}

export interface EnabledView {
  enabled?: boolean;
}

export interface ActivityIdentitiesView {
  activities?: ActivityIdentityView[];
}

export interface ActivityIdentityView {
  id?: string;

  name?: string;

  templateCss?: string;

  templateHtml?: string;

  createdAt?: number;

  updatedAt?: number;

  assignedToCampaigns?: boolean;

  isFavorite?: boolean;

  editorType?: "DRAG_AND_DROP" | "HTML";

  ampErrors?: string[];
}

export interface ActivityDetailsView {
  id?: string;

  name?: string;

  subject?: string;

  html?: string;

  css?: string;

  templateCss?: string;

  templateHtml?: string;

  tags?: TemplateTagView[];

  editorType?: "DRAG_AND_DROP" | "HTML";

  activityTypeView?: "EMAIL_TEMPLATE" | "EMAIL_MESSAGE";

  ampErrors?: string[];

  skipAmpErrorsCheck?: boolean;

  compressTemplate?: boolean;
}

export interface TemplateTagView {
  id?: string;

  name?: string;
}

export interface IdView {
  id?: string;

  ampErrors?: string[];
}

export interface IdsView {
  ids?: string[];
}

export interface CampaignFilterView {
  from?: number;

  to?: number;

  searchTextName?: string;

  campaignStatuses?: (
    | "DRAFT"
    | "LAUNCHED"
    | "QUEUED"
    | "COMPLETED"
    | "SCHEDULED"
    | "PAUSED"
    | "DELETED"
    | "FAILED"
    | "RECURRING"
    | "STOPPED"
  )[];

  ids?: string[];
  parentCampaignId?: string;
  showCampaignRevenue?: boolean;
}
export interface DomainEngagementFilterView {
  domainName?: string;
  campaignId: string;
  allowRegexSearch?: boolean;
}

export interface RecurringChildrenFilterView {
  from?: number;

  to?: number;

  searchTextName?: string;

  campaignStatuses?: (
    | "DRAFT"
    | "LAUNCHED"
    | "QUEUED"
    | "COMPLETED"
    | "SCHEDULED"
    | "PAUSED"
    | "DELETED"
    | "FAILED"
    | "RECURRING"
    | "STOPPED"
  )[];

  ids?: string[];
}

export interface DomainDeliverabilityFilterView {
  domainName?: string;
  campaignId: string;
  allowRegexSearch?: boolean;
}

export interface SubAccountUsageView {
  accountType: string;
  accountId: string;
  userEmail: string;
  usages: number;
  subscriptionName: string;
  scheduledSubscriptionName: string;
}

export interface SubAccountUsageResponseView {
  accountUsages: SubAccountUsageView[];
  /**
   * Group total: the master account's own sending plus every sub-account's.
   * Each `accountUsages` row — the MASTER one included — carries only that
   * account's own usage.
   */
  totalUsages?: number | null;
}

export interface CreateSubAccountView {
  loginEmail: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  company: string;
  site: string;
  address: string;
  country: string;
  city: string;
  state: string;
  postalCode?: string;
}

export interface SubAccountView {
  id: string;
  userEmail: string;
  company: string;
  accountType: string;
  masterAccountId: string;
  accountStatus: string;
}

export interface CampaignInfoView {
  id?: string;

  accountId?: string;

  name?: string;

  subject?: string;

  smartAssistant?: boolean;

  start?: number;

  completed?: number;

  sent?: number;

  requests?: number;

  totalOpen?: number;

  open?: number;

  totalClicked?: number;

  clicked?: number;

  unsubscribed?: number;

  delivered?: number;

  bounced?: number;

  spam?: number;

  total?: number;

  sentProgress?: number;

  openProgress?: number;

  totalOpenProgress?: number;

  clickedProgress?: number;

  totalClickedProgress?: number;

  unsubscribedProgress?: number;

  deliveredProgress?: number;

  bouncedProgress?: number;

  spamProgress?: number;

  totalProgress?: number;

  campaignStatus?:
    | "DRAFT"
    | "LAUNCHED"
    | "QUEUED"
    | "COMPLETED"
    | "SCHEDULED"
    | "PAUSED"
    | "DELETED"
    | "FAILED"
    | "RECURRING"
    | "STOPPED";

  /**
   * When the campaign was paused mid-send; absent unless currently paused. Resume is only offered for a limited window
   * after this (24h by default) because the leftover audience in ClickHouse expires, so the UI uses it to stop offering
   * a resume the backend would refuse with a 409.
   */
  pausedAt?: number;

  /**
   * Recurrence schedule, populated only on a recurring campaign (mapped from the campaign's own `dayOfWeek`, so a
   * recurring campaign's individual child sends have this empty). Non-empty is the discriminator between a recurring
   * campaign and a normal one — `campaignStatus` cannot tell them apart, because STOPPED is the terminal state of both
   * a stopped recurrence and a stopped in-flight send.
   */
  days?: string[];

  time?: string;

  createdAt?: number;

  updatedAt?: number;

  editedAt?: number;

  totalRevenue?: number;
}

export interface CampaignInfoViewBasic {
  id?: string;
  name?: string;
}

export interface DomainEngagementInfoView {
  send: number;
  domainName: string;
  delivered: number;
  opens: number;
  clicks: number;
}
export interface DomainDeliverabilityInfoView {
  send: number;
  domainName: string;
  delivered: number;
  spams: number;
  unsubscribes: number;
}
export interface PageDomainEngagementInfoView {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  numberOfElements?: number;

  pageable?: Pageable;

  first?: boolean;

  last?: boolean;

  size?: number;

  content?: DomainEngagementInfoView[];

  number?: number;

  empty?: boolean;
}

export interface PageDomainDeliverabilityInfoView {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  numberOfElements?: number;

  pageable?: Pageable;

  first?: boolean;

  last?: boolean;

  size?: number;

  content?: DomainDeliverabilityInfoView[];

  number?: number;

  empty?: boolean;
}
export interface PageCampaignInfoView {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  numberOfElements?: number;

  pageable?: Pageable;

  first?: boolean;

  last?: boolean;

  size?: number;

  content?: CampaignInfoView[];

  number?: number;

  empty?: boolean;
}

export interface PageCampaignInfoViewBasic {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  numberOfElements?: number;

  pageable?: Pageable;

  first?: boolean;

  last?: boolean;

  size?: number;

  content?: CampaignInfoViewBasic[];

  number?: number;

  empty?: boolean;
}

export interface ActivityFilterView {
  searchNameText?: string;

  tagIds?: string[];

  isFavorite?: boolean;

  editorType?: "DRAG_AND_DROP" | "HTML";
}

export interface PageActivityIdentityView {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  numberOfElements?: number;

  pageable?: Pageable;

  first?: boolean;

  last?: boolean;

  size?: number;

  content?: ActivityIdentityView[];

  number?: number;

  empty?: boolean;
}

export interface EmailActivityTemplateFilterView {
  searchNameText?: string;

  types?: ("BASIC" | "CUSTOM" | "LIBRARY" | "STAFF_PICKED")[];

  tagIds?: string[];

  isFavorite?: boolean;
}
/**
 * Membership audit actions known at the time of writing. The list is additive
 * on the server, so anything unrecognised must still render — fall back to the
 * entry's `message` rather than dropping the row.
 */
export const KNOWN_ACCOUNT_MEMBER_AUDIT_ACTIONS = [
  "INVITED",
  "INVITATION_RESENT",
  "INVITATION_ACCEPTED",
  "ROLE_CHANGED",
  "EMAIL_CHANGED",
  "DEACTIVATED",
  "REACTIVATED",
  "STATUS_CHANGED",
  "REMOVED",
] as const;

export type KnownAccountMemberAuditAction =
  typeof KNOWN_ACCOUNT_MEMBER_AUDIT_ACTIONS[number];

/** One membership change. Null fields are omitted from the JSON. */
export interface AccountMemberAuditView {
  id: string;
  actorMemberId?: string;
  /**
   * Null on INVITATION_ACCEPTED: the invitee accepts on an unauthenticated
   * endpoint, so there is nobody else to name.
   */
  actorEmail?: string;
  /** May point at a member that no longer exists — history outlives removal. */
  targetMemberId?: string;
  /** On EMAIL_CHANGED this is the OLD address; the new one is in `details`. */
  targetEmail?: string;
  /** Additive: treat unknown values as renderable via `message`. */
  action: string;
  rolesBefore?: AccountRole[];
  rolesAfter?: AccountRole[];
  statusBefore?: string;
  statusAfter?: string;
  /** On EMAIL_CHANGED, the new address. */
  details?: string;
  ipAddress?: string;
  createdAt?: number;
  /** Server-rendered and ready to display. Prefer this over composing text. */
  message?: string;
}

export interface PageAccountMemberAuditView {
  totalPages?: number;
  totalElements?: number;
  sort?: Sort;
  numberOfElements?: number;
  pageable?: Pageable;
  first?: boolean;
  last?: boolean;
  size?: number;
  content?: AccountMemberAuditView[];
  number?: number;
  empty?: boolean;
}

export interface AccountMemberCreateView {
  email: string;
  /** @deprecated Send `roles`. Kept only for backward compatibility. */
  role?: AssignableAccountRole;
  /**
   * Wins over `role` when both are sent. Naming neither is rejected with 412,
   * and so is naming SUPER_ADMIN.
   */
  roles?: AssignableAccountRole[];
}

export interface AccountMemberUpdateView {
  id: string;
  /**
   * Required, despite the API docs saying a deactivation may send `id` +
   * `status` alone: stage 500s on that payload (verified against
   * api-stage.tiny-email.com), and succeeds once `email` and `roles` are
   * present. Keeping it non-optional stops anyone rebuilding the payload that
   * breaks. Sending a DIFFERENT address resets the member to INVITED and
   * re-sends the invitation.
   */
  email: string;
  /** @deprecated Send `roles`. Kept only for backward compatibility. */
  role?: AssignableAccountRole;
  /**
   * Wins over `role` when both are sent. Omitting BOTH leaves the member's
   * roles untouched, which is how email/status are edited on their own.
   * Naming SUPER_ADMIN is rejected with 412, as is editing the owner's roles.
   */
  roles?: AssignableAccountRole[];
  status:
    | "ACTIVE"
    | "CANCELED"
    | "DELETED"
    | "EXPIRED"
    | "INACTIVE"
    | "ACCEPTED"
    | "INVITED";
}

export interface EmailActivityTemplateIdentityView {
  id?: string;

  name?: string;

  templateCss?: string;

  templateHtml?: string;

  createdAt?: number;

  updatedAt?: number;

  isFavorite?: boolean;
}

export interface PageEmailActivityTemplateIdentityView {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  numberOfElements?: number;

  pageable?: Pageable;

  first?: boolean;

  last?: boolean;

  size?: number;

  content?: EmailActivityTemplateIdentityView[];

  number?: number;

  empty?: boolean;
}

export interface AccountView {
  id?: string;

  userEmail?: string;

  firstName?: string;

  lastName?: string;

  phoneNumber?: string;

  site?: string;

  company?: string;

  address?: string;

  country?: string;

  postalCode?: string;

  city?: string;

  state?: string;

  accountStatus?:
    | "ACTIVE"
    | "FROZEN"
    | "PAUSED"
    | "LATE"
    | "LATE_TRIAL"
    | "CANCELED";

  dkimAdded?: boolean;

  tfaStatus?: boolean;
}

export interface AccountIntegrationExecutionView {
  integrationSystemName?: string;

  errorMessage?: string;

  errorMessageDetails?: string;

  importedCustomers?: number;
}

export interface ConfigurableNotificationUpdateView {
  name?: string;

  disabled?: boolean;
}

export interface ConfigurableNotificationCategoryUpdateView {
  name?: string;

  disabled?: boolean;
}

export interface SubscriptionCostCalculateParametersView {
  tierId?: string;

  promotionCode?: string;

  yearly?: boolean;

  country?: string;

  postalCode?: string;

  isTrial?: boolean;
}

export interface SubscriptionCostView {
  subtotal?: number;

  tax?: number;

  discount?: number;

  taxDetails?: TaxRateView[];

  total?: number;

  nextCyclePrice?: number;
}

export interface TaxRateView {
  id?: string;

  name?: string;

  inclusive?: boolean;

  percentage?: number;
}

export interface AttachBillingInformationView {
  firstName: string;

  lastName: string;

  address: string;

  country: string;

  postalCode: string;

  city: string;

  state: string;

  taxType?: string;

  taxId?: string;
}

export interface TicketCreateRequestDto {
  ticketType?: "CAMPAIGN_REVIEW";

  accountId?: string;

  campaignId?: string;

  campaignName?: string;

  accountEmail?: string;
}

export interface LeadView {
  email?: string;

  plan?: string;

  subscriptionType?: string;

  emailVolume?: string;

  trial?: boolean;

  promoCode?: string;

  affiliateId?: string;

  utmSource?: string;

  utmMedium?: string;

  utmCampaign?: string;

  utmTerm?: string;

  utmContent?: string;

  skipC?: boolean;

  planCode?: string;
}

export interface LicenseView {
  action?: string;

  plan_id?: string;

  uuid?: string;

  activation_email?: string;

  invoice_item_uuid?: string;
}

export interface LicenseCreatedView {
  message?: string;

  redirect_url?: string;
}

export interface SenderDetailsCreateView {
  name?: string;

  email?: string;

  address?: string;

  city?: string;

  region?: string;

  country?: string;

  postalCode?: string;

  defaults?: boolean;

  sendConfirmationEmail?: boolean;
}

export interface SenderDetailsEmailConfirmationView {
  token?: string;
}

export interface AttachPaymentMethodView {
  cardNumber: string;

  expirationMonth: number;

  expirationYear: number;

  cvcCode: string;

  country: string;

  postalCode: string;

  makeDefault?: boolean;
}

export interface AddressResponse {
  city?: string;

  country?: string;

  line1?: string;

  line2?: string;

  postalCode?: string;

  state?: string;
}

export interface BillingDetailsResponse {
  address?: AddressResponse;

  email?: string;

  name?: string;

  phone?: string;
}

export interface CardResponse {
  last4?: string;

  expMonth?: string;

  expYear?: string;

  fingerprint?: string;

  brand?: string;
}

export interface LastSetupError {
  code?: string;

  declineCode?: string;

  errorType?: string;

  message?: string;
}

export interface PaymentMethodResponse {
  id?: string;

  card?: CardResponse;

  billingDetails?: BillingDetailsResponse;

  shippingDetails?: BillingDetailsResponse;
}

export interface PaymentMethodView {
  id?: string;

  paymentMethodStatus?:
    | "VALIDATING"
    | "ACTIVE"
    | "REQUIRES_PAYMENT_METHOD"
    | "REQUIRES_ACTION";

  lastSetupError?: LastSetupError;

  paymentDetails?: PaymentMethodResponse;

  defaults?: boolean;
}

export interface CampaignCreateRequestView {
  name?: string;

  subject?: string;

  preview?: string;

  smartAssistant?: boolean;

  sendOrder?: CampaignSendOrder;
}

export type AbTestStatus = "PENDING" | "SCHEDULED" | "RUNNING" | "COMPLETED";

export type WinningMetric = "OPEN_RATE" | "CLICK_RATE";

export interface AbTestView {
  abTestId: string;
  parentCampaignId: string;
  name: string;
  variationAId?: string | null;
  variationBId?: string | null;
  winnerCampaignId?: string | null;
  remainingCampaignId?: string | null;
  winningMetric: WinningMetric;
  limitValue?: number | null;
  testDurationHours: number;
  variationSplit: number;
  status: AbTestStatus;
  winnerEvaluationAt?: string | null;
  launchedAt?: string | null;
  evaluatedAt?: string | null;
}

export interface AbTestCreateRequestView {
  name: string;
  winningMetric: WinningMetric;
  variationSplit: number;
  testDurationHours: number;
  limitValue?: number | null;
}

export type AbTestUpdateRequestView = Partial<AbTestCreateRequestView>;

export interface AbTestVariationsRequestView {
  variationAId: string;
  variationBId: string;
}

export interface PageAbTestView {
  content: AbTestView[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

export interface CampaignCreateByTemplateRequestView {
  templateCampaignId?: string;
}

export interface CampaignStatisticsTotalRequestView {
  from?: number;

  to?: number;
}

export interface CampaignStatisticsTotalResponseView {
  completed?: number;

  queued?: number;

  launched?: number;

  scheduled?: number;

  paused?: number;

  requests?: number;

  sent?: number;

  open?: number;

  totalOpen?: number;

  clicked?: number;

  totalClicked?: number;

  unsubscribed?: number;

  delivered?: number;

  bounced?: number;

  spam?: number;

  sentProgress?: number;

  openProgress?: number;

  totalOpenProgress?: number;

  clickedProgress?: number;

  totalClickedProgress?: number;

  unsubscribedProgress?: number;

  deliveredProgress?: number;

  bouncedProgress?: number;

  spamProgress?: number;
}

export interface CampaignStatisticsGroupedRequestView {
  from?: number;

  to?: number;

  groupTimeUnit?: "HOUR" | "DAY" | "MONTH" | "YEAR";
}

export interface CampaignStatisticsGroupedResponseView {
  timeGroupStatisticsList?: CampaignTimeGroupStatisticsView[];
}

export interface CampaignTimeGroupStatisticsView {
  campaignStart?: number;

  sent?: number;

  open?: number;

  clicked?: number;

  unsubscribed?: number;

  delivered?: number;

  bounced?: number;

  spam?: number;
}

export interface CampaignCloneRequestView {
  cloneCampaignId?: string;
}

export interface AccountCreateView {
  id?: string;

  userEmail?: string;

  firstName?: string;

  lastName?: string;

  phoneNumber?: string;

  site?: string;

  company?: string;

  address?: string;

  country?: string;

  postalCode?: string;

  city?: string;

  state?: string;

  accountStatus?:
    | "ACTIVE"
    | "FROZEN"
    | "PAUSED"
    | "LATE"
    | "LATE_TRIAL"
    | "CANCELED";

  dkimAdded?: boolean;
}

export interface AccountIntegrationView {
  integrationSystemName?: string;

  workflowName?: string;

  workflowTriggerPath?: string;

  workflowDeletePath?: string;

  properties?: Record<string, string>;
}

export interface AssignSubscriptionView {
  tierId: string;

  trial?: boolean;

  yearly?: boolean;

  promotionCode?: string;
}

export interface AssignSubscriptionWithPaymentMethodView {
  subscription: AssignSubscriptionView;

  paymentMethod: AttachPaymentMethodView;

  billingInformation: AttachBillingInformationView;
}

export interface AssignSubscriptionWithBillingInfoView {
  subscription?: AssignSubscriptionView;

  billingInformation?: AttachBillingInformationView;
}

export interface TemplateTagsView {
  tags?: TemplateTagView[];
}

export interface SubscriptionFeatureView {
  name?: string;

  featureSystemName?:
    | "STRIPO_EDITOR"
    | "STRIPO_EDITOR_CUSTOM"
    | "BASIC_TEMPLATES"
    | "CUSTOM_TEMPLATES"
    | "LIBRARY_TEMPLATES"
    | "SENDER_DETAILS"
    | "CUSTOM_SEGMENTS"
    | "PRE_BUILD_CUSTOM_SEGMENTS"
    | "REMOVE_POWERED_BY_FOOTER"
    | "SUBSCRIBER_ANALYTICS"
    | "UNIQUE_CUSTOMERS"
    | "SMART_SUBJECT_ASSISTANT"
    | "SUPPORT_CHAT"
    | "CAMPAIGN_AI_SUMMARY"
    | "AI_TRAFFIC_CONTROL";

  enabled?: boolean;

  visible?: boolean;

  featureCount?: number;

  featureCountDescription?: string;

  featureAvailabilityStatus?:
    | "AVAILABLE"
    | "NOT_AVAILABLE"
    | "PARTIAL_AVAILABLE"
    | "COMING_SOON";

  iconUrl?: string;

  parameters?: Record<string, string>;

  subscriptionName?: string;
}

export interface SubscriptionView {
  id?: string;

  name?: string;

  planCode?: string;

  logo?: string;

  bestChoice?: boolean;

  tiers?: TierView[];

  subscriptionFeatures?: SubscriptionFeatureView[];

  affiliateType?: string;
}

export interface SubscriptionsView {
  subscriptions?: SubscriptionView[];
}

export interface TierView {
  id?: string;

  name?: string;

  subscriptionName?: string;

  planCode?: string;

  tierPlanCode?: string;

  logo?: string;

  dailyCost?: number;

  monthlyCost?: number;

  yearlyCost?: number;

  overageCost?: number;

  emailsNumber?: number;

  smsNumber?: number;

  duration?: number;

  defaults?: boolean;

  subscriptionId?: string;

  customPrice?: boolean;

  overageForbidden?: boolean;
}

export interface SubscriptionFeatureAvailabilityView {
  enabled?: boolean;

  featureCount?: number;

  featureCountDescription?: string;

  featureAvailabilityStatus?:
    | "AVAILABLE"
    | "NOT_AVAILABLE"
    | "PARTIAL_AVAILABLE"
    | "COMING_SOON";

  iconUrl?: string;
}

export interface SubscriptionFeaturesAvailabilityView {
  featureName?: string;

  subscriptionAvailabilities?: Record<string, any>;
}

export interface SubscriptionFeaturesCategoriesAvailabilityView {
  categoryName?: string;

  subscriptionFeaturesAvailabilities?: SubscriptionFeaturesAvailabilityView[];
}

export interface SenderDetailsesView {
  senderDetailses?: SenderDetailsView[];
}

export interface PaymentMethodsView {
  paymentMethods?: PaymentMethodView[];
}

export interface InvoiceView {
  id?: string;

  number?: string;

  description?: string;

  invoicePdf?: string;

  amountPaid?: number;

  amountDue?: number;

  localStatus?:
    | "PAID"
    | "VOID"
    | "UNCOLLECTIBLE"
    | "OVERDUE"
    | "UPCOMING"
    | "UNKNOWN";

  status?: string;

  created?: string;

  dueDate?: string;

  periodEnd?: string;
}

export interface InvoicesView {
  invoices?: InvoiceView[];

  hasMore?: boolean;
}

export interface SocialLinksView {
  links?: string[];
}

export interface ColorsView {
  filteredVibrant?: string;

  filteredDark?: string;

  filteredLight?: string;

  suggestedColors?: string[];
}

export interface FontView {
  titleFont?: string;

  titleFallback?: string[];

  paragraphFont?: string;

  paragraphFallback?: string[];
}

export interface IdentityProfiledView {
  logo?: LogoView;

  colors?: ColorsView;

  socialLinks?: SocialLinksView;

  font?: FontView;
}

export interface LogoView {
  logoSafe?: boolean;

  logoImage?: string;

  logoSvg?: string;

  iconImage?: string;

  iconSvg?: string;
}

export interface EditorTokenView {
  token?: string;
}

export interface EntriTokenView {
  auth_token: string;
}
export interface ActivityDetailsListView {
  activities?: ActivityDetailsView[];
}

export interface ActivityEmailFooterView {
  html?: string;
}
export interface ContentLinkItem {
  url: string;
  label: "DEAD" | "UNSAFE" | string;
}
export interface EmailContentCheckResponse {
  links: ContentLinkItem[];
}

export interface EmailUnvettedCheckResponse {
  restrictUnvetted: boolean;
}

export interface ActivityTemplateAttributeView {
  name?: string;

  value?: string;
}

export interface ActivityTemplateAttributesView {
  attributes?: ActivityTemplateAttributeView[];
}

export interface CampaignIdentitiesView {
  campaigns?: CampaignIdentityView[];
}

export interface CampaignIdentityView {
  id?: string;

  name?: string;
}

export interface EmailActivityTemplateDetailsView {
  id?: string;

  name?: string;

  subject?: string;

  templateCss?: string;

  templateHtml?: string;

  tags?: TemplateTagView[];
}

export interface AccountIntegrationPropertiesItemView {
  shopDomain?: string;
  currency?: string;
}

export interface AccountIntegrationItemView {
  id?: string;

  integrationId?: string;

  accountId?: string;

  integrationName?: string;

  integrationSystemName?: string;

  workflowName?: string;

  lastExecution?: string;

  lastSuccessExecution?: string;

  lastExecutionError?: string;

  totalImported?: number;

  lastExecutionImported?: number;

  integration?: IntegrationItemView;

  properties?: AccountIntegrationPropertiesItemView;
}

export interface AccountPlatformUsageView {
  numberOfCampaigns?: number;

  numberOfSentCampaigns?: number;

  numberOfSavedTemplates?: number;

  numberOfIntegrations?: number;

  numberOfAudiences?: number;

  numberOfForms?: number;
}

export interface AccountTemplatesPlatformUsageView {
  numberOfSavedTemplates?: number;
}

export interface AccountIntegrationsPlatformUsageView {
  numberOfIntegrations?: number;
}

export interface AccountCampaignsPlatformUsageView {
  numberOfCampaigns?: number;

  numberOfSentCampaigns?: number;
}

export interface AccountAudiencesPlatformUsageView {
  numberOfAudiences?: number;
}

export interface ConfigurableNotificationCategoryView {
  name?: string;

  description?: string;

  disabled?: boolean;

  notificationSettings?: ConfigurableNotificationSettingView[];
}

export interface DomainDKIMOption {
  domain: string;
  enabled: boolean;
}
export interface ConfigurableNotificationSettingView {
  name?: string;

  description?: string;

  disabled?: boolean;
}

export interface ConfigurableNotificationSettingsView {
  notificationCategories?: ConfigurableNotificationCategoryView[];
}

export interface DomainDKIMOptions {
  domains: DomainDKIMOption[];
}
export interface DomainDKIMStatus {
  enabled: boolean;
}
export interface AccountIssueView {
  message?: string;

  code?: string;
}

export interface AccountIssuesView {
  issues?: AccountIssueView[];
}

export interface AccountSubscriptionRecommendationView {
  alert?: "ORANGE" | "RED" | "BLACK";

  daysExceeded?: boolean;

  emailUsagePercent?: number;

  trialEndsInDays?: number;
}

export interface AccountSubscriptionView {
  logo?: string;

  bestChoice?: boolean;

  subscriptionType?: "MONTH" | "YEAR" | "DAY";

  tier?: TierView;

  scheduledTier?: TierView;

  usage?: UsageView;

  recommendation?: AccountSubscriptionRecommendationView;

  promotionCode?: string;

  isTrial?: boolean;

  createdAt?: string;

  tierId?: string;

  scheduledTierId?: string;
}

export interface UsageView {
  emailsNumber?: number;
}

export interface AccountLeadSubscriptionView {
  subscriptionType?: "MONTH" | "YEAR";

  tier?: TierView;

  promotionCode?: string;

  isTrial?: boolean;

  duration?: number;

  affiliateId?: string;

  plan?: string;

  emailVolume?: string;

  utmSource?: string;

  utmMedium?: string;

  utmCampaign?: string;

  utmTerm?: string;

  utmContent?: string;

  skipC?: boolean;
}

export interface SubscriptionFeaturesView {
  subscriptionFeatures?: SubscriptionFeatureView[];
}

export interface SubscriptionFeaturesCategoriesView {
  subscriptionFeatureCategories?: SubscriptionFeaturesCategoryView[];
}

export interface SubscriptionFeaturesCategoryView {
  name?: string;

  categoryAvailabilityStatus?:
    | "AVAILABLE"
    | "NOT_AVAILABLE"
    | "PARTIAL_AVAILABLE"
    | "COMING_SOON";

  subscriptionFeatures?: SubscriptionFeatureView[];
}

export interface AccountSubscriptionBillingInfoView {
  contactInfo?: ContactInfoView;

  billingInfo?: BillingInfoView;
}

export interface BillingInfoView {
  userEmail?: string;

  firstName?: string;

  lastName?: string;

  phoneNumber?: string;

  site?: string;

  company?: string;

  address?: string;

  country?: string;

  postalCode?: string;

  city?: string;

  state?: string;

  taxId?: string;

  taxType?: string;
}

export interface ContactInfoView {
  userEmail?: string;

  firstName?: string;

  lastName?: string;

  phoneNumber?: string;

  site?: string;

  company?: string;

  address?: string;

  country?: string;

  postalCode?: string;

  city?: string;

  state?: string;
}

export interface AccountIntegrationsView {
  integrations?: AccountIntegrationItemView[];
}

export interface AccountIntegrationsSummaryView {
  accountIntegrations?: AccountIntegrationItemView[];

  foundIntegrations?: IntegrationItemView[];

  availableIntegrations?: Record<string, IntegrationItemView[]>;
}

export interface SenderDetailsDeleteView {
  ids?: string[];
}

export interface CampaignMultipleDeleteView {
  ids?: string[];
}

export interface ActivityMultipleDeleteView {
  ids?: string[];
}

export interface BaseCondition extends Condition {
  attributeType?:
    | "AUDIENCE"
    | "PROFILE"
    | "ACTIVITY"
    | "CAMPAIGN"
    | "SEGMENT"
    | "TRAFFIC_CONTROL"
    | "SENDER"
    | "WORKFLOW"
    | "CUSTOM_ATTRIBUTES"
    | "LINK_URL"
    | "UTM";

  attributeName?: string;

  criteria?: string;

  value?: ConditionValueObject;
}

export interface CombinedCondition extends Condition {
  conditionType?: "AND" | "OR";

  conditions?: Condition[];
}

export interface Condition {
  type: string;
}

export interface ConditionValueObject {
  value?: any;

  type: string;
}

export interface SegmentUpdateView {
  name?: string;

  filter?: BaseCondition | CombinedCondition;
}

export interface SegmentIdentityView1 {
  id?: string;

  name?: string;

  numberOfCustomers?: number;

  numberOfCreatedCustomers?: number;

  numberOfUpdatedCustomers?: number;

  segmentType?: string;
}
export interface ExportSegmentCreateView {
  segmentIds?: string[];
  existingStaticSegmentId?: string;
  staticSegmentName?: string;
}

export interface SuppressionUpdateView {
  name?: string;
}

export interface CustomersUpdateView {
  toAssignCustomerIds?: string[];

  toUnassignCustomerIds?: string[];
}

export interface SegmentTypeStatisticRequestView {
  types?: (
    | "UPLOAD"
    | "INTEGRATION"
    | "CUSTOM"
    | "PREDEFINED_RECOMMENDED"
    | "PREDEFINED_LIBRARY"
  )[];
}

export interface SegmentUniqueCountRequestView {
  segmentIds: string[];
  includeGlobalSuppressed: boolean;
  includeLocalSuppressed: boolean;
}

export interface SegmentSummaryIsp {
  ispName: string;
  subscribedCount: number;
  unsubscribedCount: number;
  totalCustomersCount: number;
  uniqueOpenCount: number;
  uniqueClickCount: number;
  globalSuppressed: number;
  localSuppressed: number;
}

export interface SegmentUniqueCountResponseView {
  subscribed: number;
  uniqueClicks: number;
  uniqueOpens: number;
  suppressed: number;
  isps: SegmentSummaryIsp[];
}

export interface SegmentTypeStatisticResponseView {
  countByType?: Record<string, number>;
}

export interface CustomFont {
  fontName: string;
  fontFamily: string;
  fontUrl: string;
}

export interface SegmentIdentitiesView1 {
  segments?: SegmentIdentityView1[];
}

export interface SegmentFilterView {
  from?: number;

  to?: number;

  searchTextName?: string;

  segmentTypes?: (
    | "UPLOAD"
    | "INTEGRATION"
    | "CUSTOM"
    | "FORM"
    | "PREDEFINED_RECOMMENDED"
    | "PREDEFINED_LIBRARY"
    | "PREDEFINED_DELIVERY"
  )[];

  aiEnabled?: boolean;
}

export interface SuppressionFilterView {
  searchTextName?: string;
  suppressionTypes?: ("EMAIL" | "MD5" | "SHA256")[];
  suppressionScopes?: ("LOCAL" | "GLOBAL")[];
}

export interface SuppressionDetailsFilterView {
  searchText?: string;
  suppressionId?: string;
}

export interface SegmentPageSegmentView {
  content?: SegmentView[];

  pageable?: Pageable;

  totalNumberOfCustomers?: number;

  last?: boolean;

  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  first?: boolean;

  numberOfElements?: number;

  size?: number;

  number?: number;

  empty?: boolean;
}
export interface SuppressionView {
  id?: string;
  name?: string;
  type?: "EMAIL" | "MD5" | "SHA256";
  scope?: "GLOBAL" | "LOCAL";
  suppressedCount?: number;
  updatedAt?: number;
  createdAt?: number;
  fileProcessStatus?: "UPLOADED" | "IN_PROGRESS" | "COMPLETED" | "FAILURE";
}

export interface SuppressionPageSuppressionView {
  content?: SuppressionView[];

  pageable?: Pageable;

  totalNumberOfCustomers?: number;

  last?: boolean;

  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  first?: boolean;

  numberOfElements?: number;

  size?: number;

  number?: number;

  empty?: boolean;
}

export interface SuppressionSearchResultView {
  suppressionName?: string;
  suppressionScope?: "GLOBAL" | "LOCAL";
}

export interface SuppressionSearchResponseView {
  searchSuppressed?: SuppressionSearchResultView[];
}

export interface TrafficShapingSegment {
  id: string;
  priority: number;
  name?: string;
}
export interface TrafficShapingVolume {
  domain: string;
  volume: number;
}
export interface AITrafficShapingView {
  id: string;
  name: string;
  warmupType:
    | "COLD"
    | "MIGRATION"
    | "EXISTING"
    | "NEW_SENDER_DOMAIN_IN_EXISTING_ACCOUNT";
  segments: TrafficShapingSegment[];
  volumes?: TrafficShapingVolume[];
  createdAt: number;
  campaignId?: string;
  updatedAt: number;
  assignedToCampaigns?: boolean;
  completed?: boolean;
  activateRecurring?: boolean;
  recurring?: boolean;
  engagerSegmentEnabled?: boolean;
  recurringIteration?: number;
}

export interface AITrafficControlSegmentsFilter {
  aiTrafficControlIds?: string[];
}

export interface AITrafficControlSegment {
  segmentId: string;
  audienceType: "CUSTOM" | "STANDARD";
  priority: number;
  segmentName: string;
  enabled: boolean;
  numberOfCustomers: number;
  createdAt: number;
}

export interface AITrafficControlSegmentInfo {
  aiTrafficControlId: string;
  aiTrafficControlName: string;
  recurring: boolean;
  segments: AITrafficControlSegment[];
}

export interface AITrafficControlSegmentsResponse {
  aiTrafficControlSegments: AITrafficControlSegmentInfo[];
}

export interface AITrafficControlEngagerStatsFilter {
  from: number;
  to: number;
  groupTimeUnit?: GroupTimeUnit;
  aiTrafficControlIsps: AITrafficControlIsp[];
  aiTrafficControlId: string;
}

export interface AITrafficControlEngagerStat {
  actionDate: number;
  uniqueOpen: number;
  uniqueClick: number;
}

export interface AITrafficControlEngagerStatsResponse {
  engagerStats: AITrafficControlEngagerStat[];
}

export interface EngagerConfig {
  type: string;
  thresholdDays?: number;
  senders?: string[];
}

export interface AITrafficCreateView {
  name: string;
  warmupType:
    | "COLD"
    | "MIGRATION"
    | "EXISTING"
    | "NEW_SENDER_DOMAIN_IN_EXISTING_ACCOUNT";
  segments?: TrafficShapingSegment[];
  volumes?: TrafficShapingVolume[];
  campaignId?: string;
  engagerConfig: EngagerConfig;
  autoCreateSegments?: boolean;
  activateRecurring?: boolean;
}

export interface SegmentDataExistCheckView {
  clickerMonths: number;
  openerMonths: number;
}

export interface AITrafficShapingPageView {
  content?: AITrafficShapingView[];

  pageable?: Pageable;

  totalNumberOfCustomers?: number;

  last?: boolean;

  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  first?: boolean;

  numberOfElements?: number;

  size?: number;

  number?: number;

  empty?: boolean;
}

export interface SegmentView {
  id?: string;

  name?: string;

  segmentType?:
    | "UPLOAD"
    | "INTEGRATION"
    | "CUSTOM"
    | "FORM"
    | "PREDEFINED_RECOMMENDED"
    | "PREDEFINED_LIBRARY"
    | "PREDEFINED_DELIVERY";

  numberOfCustomers?: number;

  createdAt?: number;

  updatedAt?: number;

  assignedToCampaigns?: boolean;

  assignedTo?: ("CAMPAIGN" | "FORM")[];

  description?: string;

  status?: "IN_PROGRESS" | "COMPLETED" | "FAILED" | "DELETING" | "VERIFICATION";

  aiEnabled?: boolean;

  cleaned?: boolean;
}

export interface CustomSegmentSummaryRequestView {
  customSegmentId?: string;

  condition?: BaseCondition | CombinedCondition;

  audiences?: string[];
}

export interface AudienceCriteriaView {
  criteria?: string;

  audiences?: AudienceView[];
}

export interface AudienceView {
  name?: string;

  totalSubscribers?: number;
}

export interface CustomSegmentSummaryResponseView {
  countCustomers?: number;

  countNetCustomers?: number;

  countDelivered?: number;

  countCustomersHavingOpened?: number;

  countCustomersHavingClicked?: number;

  countCustomersHavingUnsubscribed?: number;

  audiences?: AudienceCriteriaView[];

  openedPercentage: number;

  clickedPercentage: number;

  unsubscribedPercentage: number;

  isps: SegmentSummaryIsp[];
}

export interface SegmentCheckExistsFilterView {
  name?: string;

  type?:
    | "UPLOAD"
    | "INTEGRATION"
    | "CUSTOM"
    | "PREDEFINED_RECOMMENDED"
    | "PREDEFINED_LIBRARY";
}

export interface SuppressionCheckExistsFilterView {
  name?: string;

  type?: undefined | string;

  scope: "GLOBAL" | "LOCAL";
}

export interface AudienceMergeView {
  sourceAudienceIds: string[];
  targetAudienceId?: string;
  name?: string;
}

export interface SegmentMoveRequestView {
  sourceAudienceId: string;
  targetAudienceId: string;
  contactIds: string[];
  moveAll: boolean;
}

export interface SegmentMoveResultView {
  requestedCount: number;
  movedCount: number;
  newlyAddedToTarget: number;
  alreadyInTarget: number;
  notInSource: number;
}

export interface CustomerView {
  id?: string;

  email?: string;

  firstName?: string;

  lastName?: string;

  source?: string;

  birthday?: number;

  tags?: string[];

  currency?: string;

  country?: string;

  province?: string;

  city?: string;

  address1?: string;

  address2?: string;

  phone?: string;

  postalCode?: string;

  company?: string;

  timezone?: string;

  acceptsMarketing?: string;

  ordersCount?: number;

  totalSpent?: number;

  lastOrderId?: string;

  lastOrderName?: string;

  sourceId?: string;

  status?: "Subscribed" | "Unsubscribed" | "Bounced" | "Inactive" | "Spam";

  lastOrderDate?: number;

  lastOrderPrice?: number;

  actionStatus?: string;
}

export interface CustomerFilterView {
  segmentId?: string;

  segmentIds?: string[];

  statuses?: (
    | "Subscribed"
    | "Unsubscribed"
    | "Bounced"
    | "Inactive"
    | "Spam"
  )[];

  searchText?: string;

  assigned?: boolean;

  ids?: string[];

  tags?: string[];

  columns?: string[];
}

export interface CustomerStatusUpdateView {
  customerId: string;
  status: "Subscribed" | "Unsubscribed" | "Bounced" | "Inactive" | "Spam";
  statusReason?: string;
}

export interface SuppressionItemInfoView {
  id: string;
  value: string;
  description: string;
  createdAt: number;
  updatedAt: number;
}

export interface CustomerInfoView {
  id?: string;

  email?: string;

  firstName?: string;

  lastName?: string;

  status?: string;

  statusReason?: string;

  source?: string;

  birthday?: number;

  tags?: string[];

  currency?: string;

  country?: string;

  province?: string;

  city?: string;

  address1?: string;

  address2?: string;

  phone?: string;

  postalCode?: string;

  company?: string;

  timezone?: string;

  acceptsMarketing?: string;

  ordersCount?: number;

  totalSpent?: number;

  lastOrderId?: string;

  lastOrderName?: string;

  lastOrderDate?: number;

  lastOrderPrice?: number;

  send?: number;

  open?: number;

  clicked?: number;

  delivered?: number;

  unsubscribed?: number;

  createdAt?: number;

  updatedAt?: number;

  actionStatus?: string;

  customAttributes?: Record<string, string | string[]>;

  // Shape is not guaranteed (e.g. first_touch/last_touch today) — arbitrary JSON.
  utm?: Record<string, any>;
}

export interface PageCustomerInfoView {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  first?: boolean;

  last?: boolean;

  numberOfElements?: number;

  pageable?: Pageable;

  size?: number;

  content?: CustomerInfoView[];

  number?: number;

  empty?: boolean;
}

export interface SuppressionInfoView {
  id: string;
  value: string;
  description?: string;
  createdAt: number;
  updatedAt: number;
}

export interface PageSuppressionInfoView {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  first?: boolean;

  last?: boolean;

  numberOfElements?: number;

  pageable?: Pageable;

  size?: number;

  content?: SuppressionInfoView[];

  number?: number;

  empty?: boolean;
}

export interface CustomerCompositeFilterView {
  eventFilters?: FilterViewWrapperCustomerEventFilterView[];

  segmentFilters?: FilterViewWrapperCustomerSegmentFilterView[];

  propertyFilters?: FilterViewWrapperCustomerPropertyFilterView[];

  excludeFilter?: CustomerExcludeFilterView;

  searchText?: string;
}

export interface CustomerEventFilterView {
  negate?: boolean;

  eventType?:
    | "CLICK"
    | "UNIQUE_CLICK"
    | "OPEN"
    | "UNIQUE_OPEN"
    | "SEND"
    | "UNSUBSCRIBE"
    | "DELIVERED"
    | "HARD_BOUNCED"
    | "SOFT_BOUNCED"
    | "SPAM"
    | "NOT_SPAM";

  from?: number;

  to?: number;
}

export interface CustomerExcludeFilterView {
  negate?: boolean;

  customerIds?: string[];
}

export interface CustomerPropertyFilterView {
  negate?: boolean;

  propertyName?: string;
}

export interface CustomerSegmentFilterView {
  negate?: boolean;

  segmentIds?: string[];

  notAssigned?: boolean;
}

export interface FilterViewWrapperCustomerEventFilterView {
  operation?: "OR" | "AND";

  filter?: CustomerEventFilterView;
}

export interface FilterViewWrapperCustomerPropertyFilterView {
  operation?: "OR" | "AND";

  filter?: CustomerPropertyFilterView;
}

export interface FilterViewWrapperCustomerSegmentFilterView {
  operation?: "OR" | "AND";

  filter?: CustomerSegmentFilterView;
}

export interface CustomerActionPlatformFindFilterView {
  campaignId?: string;

  activityType?: "CAMPAIGN" | "AUTOMATION";

  fromDate?: number;

  toDate?: number;

  eventType?:
    | "CLICK"
    | "UNIQUE_CLICK"
    | "OPEN"
    | "UNIQUE_OPEN"
    | "SEND"
    | "UNSUBSCRIBE"
    | "DELIVERED"
    | "HARD_BOUNCED"
    | "SOFT_BOUNCED"
    | "SPAM"
    | "NOT_SPAM";
}

export interface CustomerActionPlatformInfoView {
  campaignId?: string;

  desktop?: number;

  desktopPercent?: number;

  mobile?: number;

  mobilePercent?: number;

  other?: number;

  otherPercent?: number;
}

export interface CustomerActionGeolocationFindFilterView {
  campaignId?: string;

  activityType?: "CAMPAIGN" | "AUTOMATION";

  fromDate?: number;

  toDate?: number;

  eventType?:
    | "CLICK"
    | "UNIQUE_CLICK"
    | "OPEN"
    | "UNIQUE_OPEN"
    | "SEND"
    | "UNSUBSCRIBE"
    | "DELIVERED"
    | "HARD_BOUNCED"
    | "SOFT_BOUNCED"
    | "SPAM"
    | "NOT_SPAM";

  topNth?: number;
}

export interface CustomerActionGeolocationInfoView {
  campaignId?: string;

  countryName?: string;

  countryCode?: string;

  count?: number;
}

export interface CustomerActionDomainFindFilterView {
  campaignId?: string;

  activityType?: "CAMPAIGN" | "AUTOMATION";

  fromDate?: number;

  toDate?: number;

  eventType:
    | "CLICK"
    | "UNIQUE_CLICK"
    | "OPEN"
    | "UNIQUE_OPEN"
    | "SEND"
    | "UNSUBSCRIBE"
    | "DELIVERED"
    | "HARD_BOUNCED"
    | "SOFT_BOUNCED"
    | "SPAM"
    | "NOT_SPAM";

  minPercentRate?: number;

  limit?: number;

  orderBy?: string;
}

export interface CustomerActionDomainInfoView {
  campaignId?: string;

  domain?: string;

  count?: number;

  percent?: number;
}

export interface IspBreakdownItemView {
  isp: string;
  count: number;
  percentage: number;
}

export interface CustomerSegmentInfoView {
  id?: string;

  segmentIds?: string[];

  email?: string;

  status?: string;
}

export interface PageCustomerSegmentInfoView {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  first?: boolean;

  last?: boolean;

  numberOfElements?: number;

  pageable?: Pageable;

  size?: number;

  content?: CustomerSegmentInfoView[];

  number?: number;

  empty?: boolean;
}

export interface CustomerBatchView {
  accountId?: string;

  customers?: CustomerView[];

  customAttributes?: string[];
}

export interface UploadSegmentCreateView {
  name?: string;
}

export interface SegmentTotalStatisticsResponseView {
  numberOfCustomers?: number;

  open?: number;

  clicked?: number;

  unsubscribed?: number;

  openProgress?: number;

  clickedProgress?: number;

  unsubscribedProgress?: number;
}

export interface SegmentTotalCustomersStatisticsRequestView {
  integrationLimit?: number;
}

export interface SegmentCustomersStatisticView {
  name?: string;

  numberOfCustomers?: number;
}

export interface SegmentTotalCustomersStatisticsResponseView {
  segments?: SegmentCustomersStatisticView[];
}

export interface IntegrationSegmentStatisticsGrowthRequestView {
  from?: number;

  to?: number;
}

export interface IntegrationSegmentStatisticGrowthView {
  name?: string;

  logoDefault?: string;

  total?: number;

  growth?: number;
}

export interface IntegrationSegmentStatisticsGrowthResponseView {
  integrationSegments?: IntegrationSegmentStatisticGrowthView[];
}

export interface SegmentImportView {
  id?: string;

  name?: string;

  segmentType?:
    | "UPLOAD"
    | "INTEGRATION"
    | "CUSTOM"
    | "PREDEFINED_RECOMMENDED"
    | "PREDEFINED_LIBRARY";

  customers?: CustomerView[];

  customAttributes?: string[];
}

export interface CustomSegmentCreateView {
  name?: string;

  filter?: BaseCondition | CombinedCondition;
}

export interface SegmentCloneRequestView {
  cloneSegmentId?: string;

  name?: string;
}

export interface CustomerStatisticsTotalRequestView {
  from?: number;

  to?: number;
}

export interface SuppressionStatisticsTotalRequestView {
  from?: number;

  to?: number;
}

export interface CustomerStatisticsTotalResponseView {
  fromManualUploads?: number;

  fromIntegrations?: number;

  fromManualUploadsProgress?: number;

  fromIntegrationsProgress?: number;

  numberOfUniqueCustomers?: number;
}

export interface SuppressionStatisticsTotalResponseView {
  totalGlobalSuppressed: number;
  totalLocalSuppressed: number;
}

export interface CustomerStatisticsGroupedRequestView {
  from?: number;

  to?: number;

  groupTimeUnit?: "HOUR" | "DAY" | "MONTH" | "YEAR";
}

export interface CustomerStatisticsGroupedResponseView {
  timeGroupStatisticsList?: CustomerTimeGroupStatisticsView[];
}

export interface CustomerTimeGroupStatisticsView {
  segmentType?:
    | "UPLOAD"
    | "INTEGRATION"
    | "CUSTOM"
    | "PREDEFINED_RECOMMENDED"
    | "PREDEFINED_LIBRARY";

  customerAssignedToSegmentAt?: number;

  count?: number;
}

export interface CustomerEventStatisticsGroupedRequestView {
  from?: number;

  to?: number;

  eventTypes?: (
    | "CLICK"
    | "UNIQUE_CLICK"
    | "OPEN"
    | "UNIQUE_OPEN"
    | "SEND"
    | "UNSUBSCRIBE"
    | "DELIVERED"
    | "HARD_BOUNCED"
    | "SOFT_BOUNCED"
    | "SPAM"
    | "NOT_SPAM"
  )[];

  groupTimeUnit?: "HOUR" | "DAY" | "MONTH" | "YEAR";

  parentCampaignId?: string;
}

export interface CustomerEventStatisticsGroupedResponseView {
  eventTimeGroupStatistics?: CustomerEventTimeGroupStatisticsView[];
}

export interface CustomerEventTimeGroupStatisticsView {
  eventType?:
    | "CLICK"
    | "UNIQUE_CLICK"
    | "OPEN"
    | "UNIQUE_OPEN"
    | "SEND"
    | "UNSUBSCRIBE"
    | "DELIVERED"
    | "HARD_BOUNCED"
    | "SOFT_BOUNCED"
    | "SPAM"
    | "NOT_SPAM";

  eventTime?: number;

  count?: number;

  hoursPassed?: number;
}

export interface CustomerEventStatisticsTotalRequestView {
  from?: number;

  to?: number;

  parentCampaignId?: string;
}

export interface CustomerEventStatisticsTotalResponseView {
  sent?: number;

  open?: number;

  requests?: number;

  uniqueOpen?: number;

  clicked?: number;

  uniqueClicked?: number;

  unsubscribed?: number;

  delivered?: number;

  bounced?: number;

  spam?: number;

  openProgress?: number;

  uniqueOpenProgress?: number;

  uniqueClickedProgress: number;

  clickedProgress?: number;

  unsubscribedProgress?: number;

  deliveredProgress?: number;

  bouncedProgress?: number;

  spamProgress?: number;

  blocked?: number;

  suppressed?: number;
}

export interface CustomerEventMostOpenStatisticsFilterView {
  searchText?: string;
  parentCampaignId?: string;
}

export interface CustomerEventMostOpenStatisticsResponseView {
  openEvents?: CustomerGroupedOpenEventView[];
}

export interface CustomerGroupedOpenEventView {
  email?: string;

  numberOfOpens?: number;
}

export interface CustomerEventMostClickStatisticsFilterView {
  searchText?: string;
  parentCampaignId?: string;
}

export interface CustomerEventMostClickStatisticsResponseView {
  clickEvents?: CustomerGroupedClickEventView[];
}

export interface CustomerGroupedClickEventView {
  link?: string;

  numberOfClicks?: number;
}

export interface SegmentDetailsView {
  id?: string;

  name?: string;

  segmentType?:
    | "UPLOAD"
    | "INTEGRATION"
    | "CUSTOM"
    | "PREDEFINED_RECOMMENDED"
    | "PREDEFINED_LIBRARY";

  numberOfCustomers?: number;

  createdAt?: number;

  updatedAt?: number;

  sent?: number;

  open?: number;

  clicked?: number;

  unsubscribed?: number;

  delivered?: number;

  bounced?: number;

  spam?: number;

  openProgress?: number;

  clickedProgress?: number;

  unsubscribedProgress?: number;

  deliveredProgress?: number;

  bouncedProgress?: number;

  spamProgress?: number;

  filter?: BaseCondition | CombinedCondition;

  cleaned?: boolean;
}

export interface Attribute {
  criteria?: Record<string, any>;

  name?: string;

  type?: string;

  hidden?: boolean;
}

export interface AttributeType {
  hidden?: boolean;

  attributes?: Attribute[];
}

export interface SegmentAttributesView {
  attributesTypes?: Record<string, any>;
}

export interface SegmentsView {
  segments?: SegmentView[];
}

export interface TagsView {
  tags?: string[];
}

export interface CustomerTotalView {
  totalDistinct?: number;

  totalActive?: number;
}

export interface SegmentMultipleDeleteView {
  ids?: string[];
}
export interface SuppressionMultipleDeleteView {
  ids?: string[];
}
export interface SegmentUnassignCustomersView {
  ids?: string[];
}

export interface SubjectLineFilterView {
  searchText?: string;

  isFavorite?: boolean;
}

export interface PageSubjectLineView {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  numberOfElements?: number;

  pageable?: Pageable;

  first?: boolean;

  last?: boolean;

  size?: number;

  content?: SubjectLineView[];

  number?: number;

  empty?: boolean;
}

export interface SubjectLineView {
  id?: string;

  text?: string;

  isFavorite?: boolean;
}

export interface SubjectGenerateRequestView {
  productName?: string;

  description?: string;

  toneId?: string;

  mergeTagIds?: string[];
}

export interface SubjectGenerateResponseView {
  totalAvailable?: number;

  totalUsed?: number;

  subjects?: SubjectLineView[];
}

export interface ToneView {
  id?: string;

  name?: string;

  description?: string;
}

export interface TonesView {
  tones?: ToneView[];
}

export interface MergeTagView {
  id?: string;

  label?: string;

  value?: string;
}

export interface MergeTagsView {
  mergeTags?: MergeTagView[];
}

export interface SubjectLineFeatureInfoView {
  generalInfo?: string;

  submitInfo?: string;

  totalAvailable?: number;

  totalUsed?: number;
}

export interface FormUpdateRequestView {
  name?: string;

  audienceId?: string;

  formType?: "EMBEDDED" | "POPUP" | "FLYOUT";

  formStatus?: "DRAFT" | "ACTIVE" | "INACTIVE";
}

export interface FormUpdateResponseView {
  id?: string;

  name?: string;

  audienceName?: string;

  formType?: "EMBEDDED" | "POPUP" | "FLYOUT";

  formStatus?: "DRAFT" | "ACTIVE" | "INACTIVE";
}

export interface FormFilterView {
  searchText?: string;

  formStatuses?: ("DRAFT" | "ACTIVE" | "INACTIVE")[];

  formTypes?: ("EMBEDDED" | "POPUP" | "FLYOUT")[];
}

export interface FormInfoView {
  id?: string;

  name?: string;

  formType?: "EMBEDDED" | "POPUP" | "FLYOUT";

  formStatus?: "DRAFT" | "ACTIVE" | "INACTIVE";

  lastSubmittedAt?: number;

  numberOfSubmissions?: number;

  updatedAt?: number;
}

export interface PageFormInfoView {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  numberOfElements?: number;

  pageable?: Pageable;

  first?: boolean;

  last?: boolean;

  size?: number;

  content?: FormInfoView[];

  number?: number;

  empty?: boolean;
}

export interface CategoryElementView extends UiSchemaElementView {
  label?: string;
}

export interface ControlElementView extends UiSchemaElementView {
  label?: any;

  scope?: string;
}

export interface FormAppearanceView {
  width?: string;

  height?: string;

  backgroundColor?: string;

  font?: string;

  borderWidth?: string;

  matchWebsite?: boolean;

  successMessage?: string;

  formType?: "EMBEDDED" | "POPUP" | "FLYOUT";

  triggerDetails?: FormTriggerDetailsView;

  doubleOptIn?: boolean;

  captureUtm?: boolean;

  redirectUrl?: string;
}

export interface FormConfigView {
  formId?: string;

  audienceId?: string;

  formType?: "EMBEDDED" | "POPUP" | "FLYOUT";

  allowedDomains?: string[];

  generatedCode?: string;

  payload?: FormPayloadView;

  isDirty?: boolean;
}

export interface FormContentView {
  schema?: JsonSchemaView;

  uiSchema?:
    | CategoryElementView
    | ControlElementView
    | GroupElementView
    | LabelElementView
    | LayoutElementView;

  data?: Record<string, any>;
}

export interface FormPayloadView {
  formAppearance?: FormAppearanceView;

  formContent?: FormContentView;
}

export interface FormTriggerDetailsView {
  triggerType?: "PAGE_LOAD" | "RULE" | "CUSTOM";

  triggerEvents?: FormTriggerEventView[];
}

export interface FormTriggerEventView {
  triggerEventType?: "PAGE_LEAVE" | "TIMEOUT" | "SCROLL";

  options?: Record<string, any>;
}

export interface GroupElementView extends UiSchemaElementView {
  label?: string;
}

export interface JsonSchemaView {
  id?: string;

  title?: string;

  description?: string;

  multipleOf?: number;

  maximum?: number;

  exclusiveMaximum?: boolean;

  minimum?: number;

  exclusiveMinimum?: boolean;

  maxLength?: number;

  minLength?: number;

  pattern?: string;

  additionalItems?: any;

  items?: any;

  maxItems?: number;

  minItems?: number;

  uniqueItems?: boolean;

  maxProperties?: number;

  minProperties?: number;

  required?: string[];

  additionalProperties?: any;

  definitions?: Record<string, any>;

  properties?: Record<string, any>;

  patternProperties?: Record<string, any>;

  dependencies?: Record<string, any>;

  type?: string;

  allOf?: JsonSchemaView[];

  anyOf?: JsonSchemaView[];

  oneOf?: JsonSchemaView[];

  not?: JsonSchemaView;

  format?: string;

  readOnly?: boolean;

  writeOnly?: boolean;

  examples?: any[];

  contains?: JsonSchemaView;

  propertyNames?: JsonSchemaView;

  errorMessage?: any;

  $ref?: string;

  $schema?: string;

  default?: any;

  enum?: string[];

  const?: any;

  if?: JsonSchemaView;

  then?: JsonSchemaView;

  else?: JsonSchemaView;
}
export interface LabelElementView extends UiSchemaElementView {
  text?: string;
}
export interface LayoutElementView extends UiSchemaElementView {
  elements?: UiSchemaElementView[];
}
export interface UiSchemaElementView {
  type?: string;

  rule?: any;

  options?: Record<string, any>;
}
export interface FormAllowedDomainsView {
  allowedDomains?: string[];
}
export interface FormCheckAssignedAudiencesRequestView {
  audienceIds?: string[];
}
export interface FormCheckAssignedAudiencesResponseView {
  audienceIds?: string[];
}
export interface FormTemplateFilterView {
  searchNameText?: string;

  types?: ("EMBEDDED" | "POPUP" | "FLYOUT")[];

  tagIds?: string[];

  isFavorite?: boolean;
}
export interface FormTemplateInfoView {
  id?: string;

  name?: string;

  formType?: "EMBEDDED" | "POPUP" | "FLYOUT";

  payLoad?: string;

  isFavorite?: boolean;
}
export interface PageFormTemplateInfoView {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  numberOfElements?: number;

  pageable?: Pageable;

  first?: boolean;

  last?: boolean;

  size?: number;

  content?: FormTemplateInfoView[];

  number?: number;

  empty?: boolean;
}
export interface FormView {
  name?: string;

  audienceId?: string;

  formType?: "EMBEDDED" | "POPUP" | "FLYOUT";
}
export interface FormIdentityView {
  id?: string;
}
export interface FormCloneRequestView {
  cloneFormId?: string;
}
export interface FormCloneResponseView {
  id?: string;

  name?: string;
}
export interface FormWithTemplateView {
  name?: string;

  audienceId?: string;

  formTemplateId?: string;
}
export interface FormPlatformUsageView {
  numberOfForms?: number;
}
export interface FormDetailsView {
  formId?: string;

  name?: string;

  formType?: "EMBEDDED" | "POPUP" | "FLYOUT";

  formStatus?: "DRAFT" | "ACTIVE" | "INACTIVE";

  allowedDomains?: string[];

  generatedCode?: string;

  audienceId?: string;

  payload?: FormPayloadView;

  isDirty?: boolean;
}
export interface FormDeleteRequestView {
  formIds?: string[];
}
export interface AccountAutomationView {
  id?: string;

  name?: string;

  description?: string;

  infoDetails?: string;

  enabled?: boolean;

  comingSoon?: boolean;

  emailTemplate?: AutomationTemplateView;

  workflowId?: string;
}
export interface AutomationTemplateView {
  templateCss?: string;

  templateHtml?: string;
}
export interface AutomationsView {
  automations?: AccountAutomationView[];
}

export interface DomainLevelMetric {
  eventTime: number;
  sender: string;
  isp: string;
  send: number;
  sendPercent: number;
  delivered: number;
  deliveredPercent: number;
  open: number;
  openPercent: number;
  click: number;
  clickPercent: number;
  spam: number;
  spamPercent: number;
  spamPercentOverOpen: number;
  unsubscribe: number;
  unsubscribePercent: number;
  activityType: string;
}

export interface DomainLevelMetricsResponse {
  domainLevelMetrics: DomainLevelMetric[];
}

export interface DomainLevelMetricsFilter {
  from: number;
  to: number;
  timeZone: string;
  activityTypes: ("WORKFLOW" | "CAMPAIGN" | "AUTOMATION")[];
}

export interface AIInsightsFilter extends DomainLevelMetricsFilter {
  domains: string[];
}

export interface RelayStatItem {
  date: string;
  sent: number;
  delivered: number;
  deliveryRate: number;
  open: number;
  openRate: number;
  click: number;
  clickRate: number;
  spam: number;
  spamRate: number;
  unsubscribe: number;
  unsubscribeRate: number;
}

export interface RelayStatsFilter {
  from: number;
  to: number;
}

export interface RelayAccount {
  relayId: string;
  name: string;
  maskedApiKey: string;
}

export interface RelayApiKeyResponse {
  apiKey: string;
}

export interface ApiKeyView {
  id: number;
  apiKey: string;
  status: "ACTIVE" | "INACTIVE" | "DELETED" | "BLOCKED";
  scopes: string[];
}

export interface ApiKeyCreateRequestView {
  scopes?: string[];
}

export interface ApiKeyUpdateRequestView {
  id: number;
  status: "ACTIVE" | "INACTIVE" | "DELETED" | "BLOCKED";
}

export interface RelayManagementPageResponse {
  content: RelayAccount[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

export interface RelayStatsResponse {
  content: RelayStatItem[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export interface WorkflowFilterView {
  searchTextName?: string;

  workflowStatuses?: ("DRAFT" | "ACTIVE" | "INACTIVE" | "PAUSED" | "DELETED")[];

  ids?: string[];
}

export interface WorkflowInfoView {
  id: string;
  name: string;
  createdAt?: number;
  updatedAt: number;
  status: "DRAFT" | "ACTIVE" | "INACTIVE" | "PAUSED" | "DELETED";
  generatedConversion: number;
  generatedRevenue: number;
  users?: number;
  openRate?: number;
  clickRate?: number;
  sent?: number;
  spamRate: number;
  unsubscribeRate: number;
}

export interface PageWorkflowInfoView {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  numberOfElements?: number;

  pageable?: Pageable;

  first?: boolean;

  last?: boolean;

  size?: number;

  content?: WorkflowInfoView[];

  number?: number;

  empty?: boolean;
}

export type AITrafficControlIsp =
  | "YAHOO"
  | "GOOGLE"
  | "APPLE"
  | "ATT"
  | "MICROSOFT"
  | "COMCAST"
  | "OTHER";

export type RampUpAdjustmentAction =
  | "CONTINUE_RAMP_UP"
  | "HOLD_RAMP_UP"
  | "PULLBACK";

export type AITrafficEventType =
  | "CLICK"
  | "UNIQUE_CLICK"
  | "OPEN"
  | "UNIQUE_OPEN"
  | "SEND"
  | "UNSUBSCRIBE"
  | "DELIVERED"
  | "HARD_BOUNCED"
  | "SOFT_BOUNCED"
  | "SPAM"
  | "UNIQUE_OPEN_PERCENTAGE"
  | "NOT_SPAM";

export type GroupTimeUnit = "DAY" | "WEEK" | "MONTH";

export interface AITrafficControlLogsFilter {
  from: number;
  to: number;
  aiTrafficControlIsps: AITrafficControlIsp[];
  rampUpAdjustmentActions?: RampUpAdjustmentAction[];
  groupTimeUnit?: GroupTimeUnit;
  aiTrafficControlIds?: string[];
}

export interface AITrafficControlStatsFilter {
  from: number;
  to: number;
  groupTimeUnit?: GroupTimeUnit;
  aiTrafficControlIsps: AITrafficControlIsp[];
  aiTrafficControlIds?: string[];
  events?: AITrafficEventType[];
}

export interface AITrafficControlLog {
  name: string;
  aiTrafficControlId: string;
  domain: AITrafficControlIsp;
  action: RampUpAdjustmentAction;
  reason: string;
  createdAt: number;
}

export interface AITrafficControlLogsResponse {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  numberOfElements?: number;

  pageable?: Pageable;

  first?: boolean;

  last?: boolean;

  size?: number;

  content?: AITrafficControlLog[];

  number?: number;

  empty?: boolean;
}

export interface ProviderMetrics {
  SEND?: number;
  UNIQUE_OPEN?: number;
  UNIQUE_OPEN_PERCENTAGE?: number;
}

export interface TrafficCounts {
  [provider: string]: ProviderMetrics;
}

export interface HistoryStat {
  actionDate: number;
  trafficCounts: TrafficCounts;
}

export interface AITrafficControlStatsResponse {
  historyStats: HistoryStat[];
}

export interface AITrafficControlSegmentProgressRequest {
  aiTrafficControlId: string;
  segmentId: string;
}

export interface AITrafficControlProgressBucket {
  bucket: string;
  customerCount: number;
}

export interface AITrafficControlProgressIspStat {
  isp: string;
  total: number;
  activeCount: number;
  inactiveCount: number;
}

export interface AITrafficControlProgressEngagerStats {
  total: number;
  activeCount: number;
  inactiveCount: number;
  byIsp: AITrafficControlProgressIspStat[];
}

export interface AITrafficControlSegmentProgressResponse {
  distributionOfSends: AITrafficControlProgressBucket[];
  engagerSegmentStats: AITrafficControlProgressEngagerStats;
  openRates: unknown | null;
}

export interface EngagementScoringAnalyticsRequest {
  segmentIds: string[];
}

export interface EngagementLevelResult {
  engagementLevel: string;
  customerCount: number;
}

export interface EngagementScoringAnalyticsResponse {
  results: EngagementLevelResult[];
}

export interface SegmentCleanupHistoryRequestView {
  segmentId: string;
}

export interface SegmentCleanupHistoryResponseView {
  id: string;
  fileName: string;
  cleanupAt: string;
}

export interface SegmentCleanupHistoriesResponseView {
  histories: SegmentCleanupHistoryResponseView[];
}

export interface CleanedFileUrlView {
  url: string;
}

export interface SegmentCleanupFileDownloadRequestView {
  cleanedHistoryFileId: string;
}

export interface SegmentCleanupHistoriesResponseView {
  histories: SegmentCleanupHistoryResponseView[];
}

export interface CleanedFileUrlView {
  url: string;
}

export interface SegmentCleanupFileDownloadRequestView {
  cleanedHistoryFileId: string;
}

export interface ProductCalculateCostRequestView {
  productId: string;
  segmentId: string;
}

export interface ProductCalculateCostResponseView {
  cost: number;
  quantity: number;
  productName: string;
  productDescription: string;
  cpm: number;
  creditCost?: number;
  originalCost?: number;
  discountPercentage?: number;
}

export interface SubscriberEngagementRequestView {
  from: number;
  to: number;
  segmentId: string;
  timeZone: string;
}

export interface SubscriberEngagementItemView {
  date?: number;
  send?: number;
  delivered?: number;
  opens?: number;
  openRateOnDelivered?: number;
  clicks?: number;
  clickRateOnDelivered?: number;
  clickRateOnOpen?: number;
  unsubscribe?: number;
  unsubscribeRateOnClick?: number;
  spam?: number;
  spamRateOnDelivered?: number;
}

export interface IspEngagementRequestView {
  targetDate: number;
  segmentId: string;
  timeZone: string;
}

export interface IspEngagementItemView {
  isp?: string;
  send?: number;
  delivered?: number;
  opens?: number;
  openRateOnDelivered?: number;
  clicks?: number;
  clickRateOnDelivered?: number;
  clickRateOnOpen?: number;
  unsubscribe?: number;
  unsubscribeRateOnClick?: number;
  spam?: number;
  spamRateOnDelivered?: number;
}

export interface CustomAttributeMergeTag {
  label: string;
  value: string;
}

export interface CustomAttributeMergeTagsView {
  customAttributeMergeTags: CustomAttributeMergeTag[];
}

export interface CustomAttributeDefinitionView {
  id: string;
  accountId: string;
  attributeName: string;
  dataType: string;
  defaultValue?: string | string[];
}

export interface CustomAttributesResponseView {
  customAttributeDefinitions: CustomAttributeDefinitionView[];
}

export interface CreateCustomAttributeRequestView {
  attributeName: string;
  dataType: string;
  defaultValue?: string | string[];
}

export interface DeleteCustomAttributeRequestView {
  customAttributeId: string;
}

export interface CreditBalanceView {
  productId: string;
  remainingCredits: number;
}

export interface RetentionIntegrationView {
  accountIntegrated: boolean;
  segmentId: string;
  reactivationSegmentId?: string | null;
  retentionScript: string;
  status: string;
}

export interface AutoTopUpConfigView {
  productId: string;
  enabled: boolean;
  thresholdCredits: number;
  topUpCredits: number;
  isCustom: boolean;
}

export interface AutoTopUpConfigUpdateView {
  enabled: boolean;
  thresholdCredits: number;
  topUpCredits: number;
}

export interface CreditPurchaseRequestView {
  productId: string;
  credits: number;
  billingInformation: any;
}

export interface CreditPurchaseResponseView {
  id: string;
  amount: number;
  status: string;
  currency: string;
  invoicePdf: string;
}

export interface CreditsUseRequestView {
  productId: string;
  segmentId: string;
}

export interface CreditsUseResponseView {
  productId: string;
  remainingCredits: number;
}

export interface CreditsCalculateRequestView {
  productId: string;
  credits: number;
}

export interface CreditsCalculateResponseView {
  cost: number;
  quantity: number;
  productName: string;
  productDescription: string;
  cpm: number;
  originalCost?: number;
  discountPercentage?: number;
}

export interface UsageHistoryItemView {
  createdAt: number;
  productName: string;
  credits: number;
  accountName: string;
}

interface SpringPage<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface ProductUsageHistoryView {
  usages: SpringPage<UsageHistoryItemView>;
}

export interface PurchaseHistoryItemView {
  createdAt: number;
  productName: string;
  credits: number;
  cost: number;
}

export interface ProductPurchaseHistoryView {
  purchases: SpringPage<PurchaseHistoryItemView>;
}

export interface ProductView {
  id: string;
  name: string;
  description?: string;
}

export interface ProductPurchaseRequestView {
  productId: string;
  segmentId: string;
  billingInformation: any;
}

export interface ProductPurchaseResponseView {
  id: string;
  amount: number;
  currency: string;
  status: string;
  invoicePdf: string;
}

export interface ProductTierView {
  name: string;
  description: string;
  productId: string;
  startingCount: number;
  endingCount: number;
  cost: number;
  minCost: number;
}

export interface DeliverabilitySettingsView {
  masterSubSpam?: boolean;
  masterSubUnsubscribe?: boolean;
  enableGlobalActiveSegmentManagement?: boolean;
  enableCustomReturnPath?: boolean;
}

export interface GlobalActiveEngagerSegmentView {
  segmentId: string;
  segmentName: string;
}

export interface GlobalActiveEngagerSettingsView {
  createdInDays?: number;
  openedInDays?: number;
  clickedInDays?: number;
  source?: string;
}

export interface PreSignedUrlGenerateRequestView {
  segmentId: string;
}

export interface PreSignedUrlGenerateResponseView {
  preSignedUrl: string;
}

export interface FormImageUploadResponseView {
  cdnUrl: string;
}

export interface WorkflowTemplatePreviewFilterView {
  id?: string;
  templateHtml?: string;
  templateCss?: string;
}

export interface WorkflowTemplatePreviewResponseView {
  id?: string;
  templateHtml?: string;
  templateCss?: string;
}

export default abstract class GeneratedApi {
  abstract request<T>({
    secure,
    path,
    type,
    query,
    format,
    body,
    ...params
  }: FullRequestParams): Promise<AxiosResponse<T>>;

  public postProductCalculateCost(data: ProductCalculateCostRequestView) {
    return this.request<ProductCalculateCostResponseView>({
      path: "/product/calculate-cost",
      method: "POST",
      body: data,
      type: ContentType.Json,
    });
  }

  public postProductPurchaseByCardId(
    card_id: string,
    data: ProductPurchaseRequestView
  ) {
    return this.request<ProductPurchaseResponseView>({
      path: `/product/purchase/${card_id}`,
      method: "POST",
      body: data,
      type: ContentType.Json,
    });
  }

  public getProductTiersByProductId(productId: string) {
    return this.request<ProductTierView[]>({
      path: `/product/${productId}/tiers`,
      method: "GET",
      type: ContentType.Json,
    });
  }

  public putSegmentCleanupHistory(data: SegmentCleanupHistoryRequestView) {
    return this.request<SegmentCleanupHistoriesResponseView>({
      path: "/segment/cleanup/history",
      method: "PUT",
      body: data,
      type: ContentType.Json,
    });
  }

  public putSegmentCleanupFileDownload(
    data: SegmentCleanupFileDownloadRequestView
  ) {
    return this.request<CleanedFileUrlView>({
      path: `/segment/cleanup/file/${data.cleanedHistoryFileId}/download`,
      method: "PUT",
      type: ContentType.Json,
    });
  }

  putSubscriptionLeadContextEmail(
    data: LeadIdentityView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: "/subscription/lead/context-email",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getSenderDetailsById(id: string, params: RequestParams = {}) {
    return this.request<SenderDetailsView>({
      path: `/sender-details/${id}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  putSenderDetailsById(
    id: string,
    data: SenderDetailsUpdateView,
    params: RequestParams = {}
  ) {
    return this.request<SenderDetailsView>({
      path: `/sender-details/${id}`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putMfaEmailOtpSetting(
    data: MfaEmailOtpRequestView,
    params: RequestParams = {}
  ) {
    return this.request<unknown>({
      path: "/account/mfa",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putMfaPreference(data: MfaPreferenceRequestView, params: RequestParams = {}) {
    return this.request<unknown>({
      path: "/account/mfa/preference",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  deleteAccountMfa(type: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/account/mfa/${type}`,
      method: "DELETE",
      type: ContentType.Json,
      ...params,
    });
  }

  deleteSenderDetailsById(id: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/sender-details/${id}`,
      method: "DELETE",

      type: ContentType.Json,
      ...params,
    });
  }

  deleteUserDetailsById(id: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/account-member/${id}`,
      method: "DELETE",

      type: ContentType.Json,
      ...params,
    });
  }

  putSenderDetailsFindFilter(
    data: SenderDetailsFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageSenderDetailsView>({
      path: "/sender-details/find/filter",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putUserDetailsFindFilter(
    data: UserDetailsFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageUserDetailsView>({
      path: "/account-member/find/filter",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putPaymentAccountPaymentMethodDefaultById(
    id: string,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/payment-account/payment-method/${id}/default`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  putPaymentAccountInvoicePayByInvoiceId(
    invoiceId: string,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/payment-account/invoice/${invoiceId}/pay`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  putPaymentAccountInvoiceSendByInvoiceId(
    invoiceId: string,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/payment-account/invoice/${invoiceId}/send`,
      method: "PUT",
      type: ContentType.Json,
      ...params,
    });
  }

  putPaymentAccountInvoiceExport(
    data: ExportInvoicesView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: "/payment-account/invoice/export",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putIntegrationFindFilter(
    data: IntegrationFilterView,
    params: RequestParams = {}
  ) {
    return this.request<IntegrationsView>({
      path: "/integration/find/filter",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getIdentityLogoById(id: string, params: RequestParams = {}) {
    return this.request<IdentityLogoView>({
      path: `/identity/${id}/logo`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  putIdentityLogoById(
    id: string,
    data: {
      logoFile?: File;
    },
    params: RequestParams = {}
  ) {
    return this.request<IdentityLogoView>({
      path: `/identity/${id}/logo`,
      method: "PUT",

      body: data,
      type: ContentType.FormData,
      ...params,
    });
  }

  deleteIdentityLogoById(id: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/identity/${id}/logo`,
      method: "DELETE",

      type: ContentType.Json,
      ...params,
    });
  }

  getIdentity(params: RequestParams = {}) {
    return this.request<IdentityView>({
      path: "/identity",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  putIdentity(
    data: {
      logoFile?: File;
      identity: IdentityView;
    },
    params: RequestParams = {}
  ) {
    return this.request<IdentityView>({
      path: "/identity",
      method: "PUT",

      body: data,
      type: ContentType.FormData,
      ...params,
    });
  }

  postSuppression(
    data: {
      file?: File;
      suppression: CreateSuppressionRequestView;
    },
    params: RequestParams = {}
  ) {
    return this.request<CreateSuppressionResponseView>({
      path: "/customer/suppression/import",
      method: "POST",

      body: data,
      type: ContentType.FormData,
      ...params,
    });
  }

  appendSuppressionFile(
    suppressionId: string,
    data: {
      file: File;
    },
    params: RequestParams = {}
  ) {
    return this.request<UploadFileView>({
      path: `/customer/suppression/${suppressionId}/append`,
      method: "POST",

      body: data,
      type: ContentType.FormData,
      ...params,
    });
  }

  postSuppressionDetails(
    suppressionId: string,
    data: {
      suppressionValues: suppressionDetailsItem[];
    },
    params: RequestParams = {}
  ) {
    return this.request<CreateSuppressionResponseView>({
      path: `/customer/suppression/${suppressionId}/suppression_values`,
      method: "POST",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postIdentity(
    data: {
      logoFile?: File;
      identity: IdentityView;
    },
    params: RequestParams = {}
  ) {
    return this.request<IdentityView>({
      path: "/identity",
      method: "POST",

      body: data,
      type: ContentType.FormData,
      ...params,
    });
  }

  getCampaignById(id: string, params: RequestParams = {}) {
    return this.request<CampaignResponseView>({
      path: `/campaign/${id}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getPredefinedSegmentCounts(id: string, params: RequestParams = {}) {
    return this.request<PredefinedSegmentsCountsResponseView>({
      path: `/pre-defined-segment/${id}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignLastChildSentById(id: string, params: RequestParams = {}) {
    return this.request<CampaignChildLastSentResponseView>({
      path: `/campaign/${id}/recurring/child/latest`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignById(
    id: string,
    data: CampaignUpdateRequestView,
    params: RequestParams = {}
  ) {
    return this.request<CampaignResponseView>({
      path: `/campaign/${id}/details`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  deleteCampaignById(id: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/campaign/${id}`,
      method: "DELETE",

      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignUnscheduleById(id: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/campaign/${id}/unschedule`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  /**
   * Halts an actively sending campaign; resumable via putCampaignResumeSendById. Distinct from
   * putCampaignUnscheduleById, which cancels a campaign that has not started sending yet. Responds 409 when the
   * campaign's workflow is not in a pausable state.
   */
  putCampaignPauseSendById(id: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/campaign/${id}/pause-send`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  /** Resumes a paused send from its checkpoint. Responds 409 unless the campaign is currently paused. */
  putCampaignResumeSendById(id: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/campaign/${id}/resume-send`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  /**
   * Terminally stops an actively sending or paused campaign; statistics are preserved and it cannot be resumed.
   * Recovery is postCampaignCloneUndeliveredById. Distinct from putCampaignStopRecurringById, which stops a recurring
   * schedule rather than an in-flight send.
   */
  putCampaignStopSendById(id: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/campaign/${id}/stop-send`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  /**
   * Creates an editable draft clone targeting only the recipients the source send never delivered to. Responds 409
   * when the source send reached everyone, i.e. there is nothing left to relaunch to.
   */
  postCampaignCloneUndeliveredById(id: string, params: RequestParams = {}) {
    return this.request<CampaignCloneUndeliveredResponseView>({
      path: `/campaign/${id}/clone-undelivered`,
      method: "POST",

      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignStopRecurringById(id: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/campaign/${id}/stop-recurring`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignResumeRecurringById(id: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/campaign/${id}/resume-recurring`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignTestEmailsById(
    id: string,
    data: CampaignTestEmailsView,
    params: RequestParams = {}
  ) {
    return this.request<CampaignRemainedTestEmailsView>({
      path: `/campaign/${id}/test-emails`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignTemplateById(id: string, params: RequestParams = {}) {
    return this.request<CampaignResponseView>({
      path: `/campaign/${id}/template`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignStartById(
    id: string,
    data: CampaignStartView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/campaign/${id}/start`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignSenderDetailsById(
    id: string,
    data: SenderDetailsIdentityView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/campaign/${id}/sender-details`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignContentSourceById(
    id: string,
    data: CampaignContentSourceRequestView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/campaign/${id}/content-source`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignSenderAllocationsById(id: string, params: RequestParams = {}) {
    return this.request<SenderAllocationsGetResponseView>({
      path: `/campaign/${id}/sender-allocations`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignSegmentsById(id: string, params: RequestParams = {}) {
    return this.request<AudienceIdentitiesView>({
      path: `/campaign/${id}/audiences`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignSuppressionsById(id: string, params: RequestParams = {}) {
    return this.request<SuppressionIdentitiesView>({
      path: `/campaign/${id}/suppressions`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignTrafficById(id: string, params: RequestParams = {}) {
    return this.request<AiTrafficShappingIdentitiesView>({
      path: `/campaign/${id}/ai_traffic_control`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignSegmentsById(
    id: string,
    data: SegmentIdentitiesView,
    params: RequestParams = {}
  ) {
    const payload = {
      audiences: data?.segments?.map((segment) => {
        return {
          id: segment?.id,
          audienceType: segment?.segmentType,
          ...segment,
        };
      }),
    };
    return this.request<void>({
      path: `/campaign/${id}/audiences`,
      method: "PUT",

      body: payload,
      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignExcludedAudiencesById(id: string, params: RequestParams = {}) {
    return this.request<AudienceIdentitiesView>({
      path: `/campaign/${id}/excluded-audiences`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignExcludedAudiencesById(
    id: string,
    data: SegmentIdentitiesView,
    params: RequestParams = {}
  ) {
    // Same body shape as the target audiences: the backend validates `audienceType`,
    // so a segment sent without one is rejected as an unknown segment type.
    const payload = {
      audiences: data?.segments?.map((segment) => {
        return {
          id: segment?.id,
          audienceType: segment?.segmentType,
          ...segment,
        };
      }),
    };
    return this.request<void>({
      path: `/campaign/${id}/excluded-audiences`,
      method: "PUT",
      body: payload,
      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignExcludedWorkflowsById(id: string, params: RequestParams = {}) {
    return this.request<ExcludedWorkflowsResponseView>({
      path: `/campaign/${id}/excluded-workflows`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignExcludedWorkflowsById(
    id: string,
    data: ExcludedWorkflowsView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/campaign/${id}/excluded-workflows`,
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignTrafficShapingById(
    id: string,
    data: {
      aiTrafficControlIds: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/campaign/${id}/ai_traffic_control`,
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignSuppressionsById(
    id: string,
    data: string[],
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/campaign/${id}/suppressions`,
      method: "PUT",

      body: {
        suppressionIds: data,
      },
      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignCheckById(
    id: string,
    data: CampaignStartView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/campaign/${id}/check`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignAutoTrackingById(id: string, params: RequestParams = {}) {
    return this.request<EnabledView>({
      path: `/campaign/${id}/auto-tracking`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignAutoTrackingById(
    id: string,
    data: EnabledView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/campaign/${id}/auto-tracking`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignActivitiesById(id: string, params: RequestParams = {}) {
    return this.request<ActivityDetailsView>({
      path: "/campaign-layout",
      method: "PUT",
      body: {
        campaignId: id,
      },
      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignActivitiesById(
    id: string,
    data: ActivityIdentitiesView,
    params: RequestParams = {}
  ) {
    const payload = data?.activities?.map((activity) => {
      const result = {
        ...activity,
        campaignId: id,
        html: activity?.templateHtml,
        css: activity?.templateCss,
      };
      delete result?.templateHtml;
      delete result?.templateCss;
      return result;
    })[0];
    return this.request<void>({
      path: "/campaign-layout",
      method: "POST",
      body: payload,
      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignActivitiesEmailMessageByCampaignId(
    campaignId: string,
    data: ActivityDetailsView,
    params: RequestParams = {}
  ) {
    const payload = {
      ...data,
      campaignId: campaignId,
      html: data?.templateHtml,
      css: data?.templateCss,
    };
    delete payload?.templateHtml;
    delete payload?.templateCss;
    return this.request<any>({
      path: "/campaign-layout",
      method: "POST",
      body: payload,
      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignSegmentsCheckAssigned(data: IdsView, params: RequestParams = {}) {
    return this.request<IdsView>({
      path: "/campaign/segments/check-assigned",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignFindFilter(
    data: CampaignFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageCampaignInfoView>({
      path: "/campaign/find/filter",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignFindFilterBasic(
    data: CampaignFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageCampaignInfoViewBasic>({
      path: "/campaign/find/filter-campaigns",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postDomainEngagementFindFilter(
    data: DomainEngagementFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageDomainEngagementInfoView>({
      path: "/campaign/domain-stats/",
      method: "POST",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postDomainDeliverabilityFindFilter(
    data: DomainDeliverabilityFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageDomainDeliverabilityInfoView>({
      path: "/campaign/domain-stats/",
      method: "POST",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignFindFilterExport(
    data: CampaignFilterView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: "/campaign/find/filter/export",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getActivityById(id: string, params: RequestParams = {}) {
    return this.request<ActivityDetailsView>({
      path: `/account-html-template/${id}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  putActivityById(
    id: string,
    data: ActivityDetailsView,
    params: RequestParams = {}
  ) {
    return this.request<ActivityIdentityView>({
      path: `/account-html-template/${id}`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putActivityFavoriteByIdAndIsFavorite(
    id: string,
    isFavorite: boolean,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/account-html-template/${id}/favorite/${isFavorite}`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  putActivityFindFilter(
    data: ActivityFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageActivityIdentityView>({
      path: "/account-html-template/find/filter",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putActivityTemplateEmailFavoriteByIdAndIsFavorite(
    id: string,
    isFavorite: boolean,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/html-template/email/${id}/favorite/${isFavorite}`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  putActivityTemplateEmailFindFilter(
    data: EmailActivityTemplateFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageEmailActivityTemplateIdentityView>({
      path: "/html-template/email/find/filter",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getAccount(params: RequestParams = {}) {
    return this.request<AccountView>({
      path: "/account",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  putAccount(data: AccountView, params: RequestParams = {}) {
    return this.request<AccountView>({
      path: "/account",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postAccount(data: AccountCreateView, params: RequestParams = {}) {
    return this.request<AccountView>({
      path: "/account",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postAccountSubAccount(
    data: CreateSubAccountView,
    params: RequestParams = {}
  ) {
    return this.request<SubAccountView>({
      path: "/account/sub-account",
      method: "POST",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  deleteAccount(
    query: {
      email: string;
    },
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: "/account",
      method: "DELETE",
      query: query,

      type: ContentType.Json,
      ...params,
    });
  }

  putAccountCanceledByAccountId(
    accountId: string,
    query: {
      reason: string;
      issueType:
        | "SUBSCRIPTION_PAYMENT"
        | "ACCOUNT_SETUP"
        | "SUBSCRIPTION_LIMIT"
        | "HIGH_BOUNCE_RATE"
        | "HIGH_SPAM_RATE"
        | "HIGH_UNSUBSCRIBE_RATE"
        | "ACCOUNT_ISSUE";
    },
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/account/${accountId}/canceled`,
      method: "PUT",
      query: query,

      type: ContentType.Json,
      ...params,
    });
  }

  putAccountAccountIntegrationExecutionByAccountId(
    accountId: string,
    data: AccountIntegrationExecutionView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/account/${accountId}/account-integration/execution`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putAccountNotificationSettingNotification(
    data: ConfigurableNotificationUpdateView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: "/account/notification-setting/notification",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putAccountNotificationSettingCategory(
    data: ConfigurableNotificationCategoryUpdateView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: "/account/notification-setting/category",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putAccountSubscriptionCalculateCost(
    data: SubscriptionCostCalculateParametersView,
    params: RequestParams = {}
  ) {
    return this.request<SubscriptionCostView>({
      path: "/account-subscription/calculate-cost",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getAccountSubscriptionBillingInfo(params: RequestParams = {}) {
    return this.request<AccountSubscriptionBillingInfoView>({
      path: "/account-subscription/billing-info",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  putAccountSubscriptionBillingInfo(
    data: AttachBillingInformationView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: "/account-subscription/billing-info",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putAccountIntegrationTriggerByIntegrationSystemName(
    integrationSystemName: string,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/account-integration/${integrationSystemName}/trigger`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  putAccountIntegrationSave(params: RequestParams = {}) {
    return this.request<void>({
      path: "/account-integration/save",
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  postTicket(data: TicketCreateRequestDto, params: RequestParams = {}) {
    return this.request<void>({
      path: "/ticket",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getSubscriptionLead(params: RequestParams = {}) {
    return this.request<LeadView>({
      path: "/subscription/lead",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  postSubscriptionLead(data: LeadView, params: RequestParams = {}) {
    return this.request<LeadIdentityView>({
      path: "/subscription/lead",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postSubscriptionAppsumoLicense(params: RequestParams = {}) {
    return this.request<LicenseCreatedView>({
      path: "/subscription/appsumo/license",
      method: "POST",

      type: ContentType.Json,
      ...params,
    });
  }

  getSenderDetails(params: RequestParams = {}) {
    return this.request<SenderDetailsesView>({
      path: "/sender-details",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getCustomFonts(params: RequestParams = {}) {
    return this.request<CustomFont[]>({
      path: "/custom-font",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getSenderDetailsByAccountId(accountId: string, params: RequestParams = {}) {
    return this.request<SenderDetailsView[]>({
      path: `/account/${accountId}/sender-details`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  postSenderDetails(data: SenderDetailsCreateView, params: RequestParams = {}) {
    return this.request<SenderDetailsView>({
      path: "/sender-details",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  deleteSenderDetails(
    data: SenderDetailsDeleteView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: "/sender-details",
      method: "DELETE",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postSenderDetailsEmailConfirmTokenBySenderDetailsId(
    senderDetailsId: string,
    params: RequestParams = {}
  ) {
    return this.request<string>({
      path: `/sender-details/${senderDetailsId}/email-confirm-token`,
      method: "POST",

      type: ContentType.Json,
      ...params,
    });
  }

  postSenderDetailsConfirmEmail(
    data: SenderDetailsEmailConfirmationView,
    params: RequestParams = {}
  ) {
    return this.request<SenderDetailsView>({
      path: "/sender-details/confirm-email",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postPaymentAccountWebhook(params: RequestParams = {}) {
    return this.request<void>({
      path: "/payment-account/webhook",
      method: "POST",

      type: ContentType.Json,
      ...params,
    });
  }

  getPaymentAccountPaymentMethod(params: RequestParams = {}) {
    return this.request<PaymentMethodsView>({
      path: "/payment-account/payment-method",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  postPaymentAccountPaymentMethod(
    data: AttachPaymentMethodView,
    params: RequestParams = {}
  ) {
    return this.request<PaymentMethodView>({
      path: "/payment-account/payment-method",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCampaign(data: CampaignCreateRequestView, params: RequestParams = {}) {
    return this.request<CampaignResponseView>({
      path: "/campaign/details",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignAbTest(
    query: {
      from?: string;
      to?: string;
      page?: number;
      size?: number;
      sort?: string;
    },
    params: RequestParams = {}
  ) {
    return this.request<PageAbTestView>({
      path: "/campaign/ab-test",
      method: "GET",
      query: query,
      type: ContentType.Json,
      ...params,
    });
  }

  postCampaignAbTest(
    data: AbTestCreateRequestView,
    params: RequestParams = {}
  ) {
    return this.request<AbTestView>({
      path: "/campaign/ab-test",
      method: "POST",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignAbTestById(abTestId: string, params: RequestParams = {}) {
    return this.request<AbTestView>({
      path: `/campaign/ab-test/${abTestId}`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  patchCampaignAbTestById(
    abTestId: string,
    data: AbTestUpdateRequestView,
    params: RequestParams = {}
  ) {
    return this.request<AbTestView>({
      path: `/campaign/ab-test/${abTestId}`,
      method: "PATCH",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  patchCampaignAbTestVariationsById(
    abTestId: string,
    data: AbTestVariationsRequestView,
    params: RequestParams = {}
  ) {
    return this.request<AbTestView>({
      path: `/campaign/ab-test/${abTestId}/variations`,
      method: "PATCH",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  deleteCampaignAbTestById(abTestId: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/campaign/ab-test/${abTestId}`,
      method: "DELETE",
      type: ContentType.Json,
      ...params,
    });
  }

  postCustomFonts(data: customFontsRequestView, params: RequestParams = {}) {
    return this.request<customFontsResponseView>({
      path: "/custom-font",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  deleteCampaign(data: CampaignMultipleDeleteView, params: RequestParams = {}) {
    return this.request<void>({
      path: "/campaign",
      method: "DELETE",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCampaignSenderDetailsEmailConfirmTokenByCampaignIdAndSenderDetailsId(
    campaignId: string,
    senderDetailsId: string,
    params: RequestParams = {}
  ) {
    return this.request<string>({
      path: `/campaign/${campaignId}/sender-details/${senderDetailsId}/email-confirm-token`,
      method: "POST",

      type: ContentType.Json,
      ...params,
    });
  }

  postCampaignTemplate(
    data: CampaignCreateByTemplateRequestView,
    params: RequestParams = {}
  ) {
    return this.request<CampaignResponseView>({
      path: "/campaign/template",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCampaignStatisticsTotal(
    data: CampaignStatisticsTotalRequestView,
    params: RequestParams = {}
  ) {
    return this.request<CampaignStatisticsTotalResponseView>({
      path: "/campaign/statistics/total",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCampaignStatisticsGrouped(
    data: CampaignStatisticsGroupedRequestView,
    params: RequestParams = {}
  ) {
    return this.request<CampaignStatisticsGroupedResponseView>({
      path: "/campaign/statistics/grouped",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCampaignClone(
    data: CampaignCloneRequestView,
    params: RequestParams = {}
  ) {
    return this.request<CampaignResponseView>({
      path: "/campaign/clone",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postActivity(data: ActivityDetailsView, params: RequestParams = {}) {
    return this.request<ActivityIdentityView>({
      path: "/account-html-template",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  deleteActivity(data: ActivityMultipleDeleteView, params: RequestParams = {}) {
    return this.request<void>({
      path: "/account-html-template",
      method: "DELETE",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postAccountAccountIntegrationByAccountId(
    accountId: string,
    data: AccountIntegrationView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/account/${accountId}/account-integration`,
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getAccountSubscription(params: RequestParams = {}) {
    return this.request<AccountSubscriptionView>({
      path: "/account-subscription",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  postAccountSubscription(
    data: AssignSubscriptionView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: "/account-subscription",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postAccountSubscriptionPaymentMethod(
    data: AssignSubscriptionWithPaymentMethodView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: "/account-subscription/payment-method",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postAccountSubscriptionPaymentMethodByPaymentMethodId(
    paymentMethodId: string,
    data: AssignSubscriptionWithBillingInfoView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/account-subscription/payment-method/${paymentMethodId}`,
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postAccountSubscriptionDefault(params: RequestParams = {}) {
    return this.request<void>({
      path: "/account-subscription/default",
      method: "POST",

      type: ContentType.Json,
      ...params,
    });
  }

  getTemplateTag(params: RequestParams = {}) {
    return this.request<TemplateTagsView>({
      path: "/template-tag",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getSubscription(params: RequestParams = {}) {
    return this.request<SubscriptionsView>({
      path: "/subscription",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getSubscriptionByTierByTierId(tierId: string, params: RequestParams = {}) {
    return this.request<SubscriptionView>({
      path: `/subscription/by-tier/${tierId}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getSubscriptionFeatureAvailable(params: RequestParams = {}) {
    return this.request<SubscriptionFeaturesAvailabilityView[]>({
      path: "/subscription-feature/available",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getSubscriptionFeatureAvailableByFeatureSystemName(
    featureSystemName: string,
    params: RequestParams = {}
  ) {
    return this.request<SubscriptionFeaturesAvailabilityView>({
      path: `/subscription-feature/available/${featureSystemName}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getSubscriptionFeatureAvailableByCategories(params: RequestParams = {}) {
    return this.request<SubscriptionFeaturesCategoriesAvailabilityView[]>({
      path: "/subscription-feature/available/by-categories",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getSenderDetailsCheckExistsByName(name: string, params: RequestParams = {}) {
    return this.request<boolean>({
      path: `/sender-details/check-exists/${name}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getPaymentAccountInvoice(
    query: {
      limit?: number;
      startAfter?: string;
    },
    params: RequestParams = {}
  ) {
    return this.request<InvoicesView>({
      path: "/payment-account/invoice",
      method: "GET",
      query: query,

      type: ContentType.Json,
      ...params,
    });
  }

  getIntegration(params: RequestParams = {}) {
    return this.request<IntegrationsView>({
      path: "/integration",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getIntegrationProfiled(params: RequestParams = {}) {
    return this.request<IntegrationsView>({
      path: "/integration/profiled",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getIdentitySocialLinksProfiled(params: RequestParams = {}) {
    return this.request<SocialLinksView>({
      path: "/identity/social-links/profiled",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getIdentityProfiled(params: RequestParams = {}) {
    return this.request<IdentityProfiledView>({
      path: "/identity/profiled",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getEditorToken(params: RequestParams = {}) {
    return this.request<EditorTokenView>({
      path: "/editor/token",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignEmailFooterByCampaignId(
    campaignId: string,
    params: RequestParams = {}
  ) {
    return this.request<ActivityEmailFooterView>({
      path: `/campaign/${campaignId}/email/footer`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignActivitiesByCampaignIdAndActivityId(
    campaignId: string,
    activityId: string,
    params: RequestParams = {}
  ) {
    return this.request<ActivityDetailsView>({
      path: "/campaign-layout",
      method: "PUT",
      body: {
        campaignId,
      },
      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignActivitiesTemplateAttributesByCampaignId(
    campaignId: string,
    params: RequestParams = {}
  ) {
    return this.request<ActivityTemplateAttributesView>({
      path: `/campaign/${campaignId}/template-attributes`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignTemplates(
    query: {
      searchTextName: string;
    },
    params: RequestParams = {}
  ) {
    return this.request<CampaignIdentitiesView>({
      path: "/campaign/templates",
      method: "GET",
      query: query,

      type: ContentType.Json,
      ...params,
    });
  }

  getActivityTemplateAttributes(params: RequestParams = {}) {
    return this.request<ActivityTemplateAttributesView>({
      path: "/account-html-template/template-attributes",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getActivityEmailFooter(params: RequestParams = {}) {
    return this.request<ActivityEmailFooterView>({
      path: "/account-html-template/email/footer",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getActivityTemplateEmailById(id: string, params: RequestParams = {}) {
    return this.request<EmailActivityTemplateDetailsView>({
      path: `/html-template/email/${id}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getAccountAccountIntegrationByAccountIdAndIntegrationSystemName(
    accountId: string,
    integrationSystemName: string,
    params: RequestParams = {}
  ) {
    return this.request<AccountIntegrationItemView>({
      path: `/account/${accountId}/account-integration/${integrationSystemName}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getAccountProfiled(params: RequestParams = {}) {
    return this.request<AccountView>({
      path: "/account/profiled",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getAccountPoweredByCheckExists(params: RequestParams = {}) {
    return this.request<boolean>({
      path: "/account/powered-by/check-exists",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getAccountNotificationSetting(params: RequestParams = {}) {
    return this.request<ConfigurableNotificationSettingsView>({
      path: "/account/notification-setting",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getDKIMDomains(params: RequestParams = {}) {
    return this.request<DomainDKIMOptions>({
      path: "/dkim/domain",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  checkDKIMDomains(domain: string, params: RequestParams = {}) {
    return this.request<DomainDKIMStatus>({
      path: `/dkim/check?domain=${domain}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  addDKIMDomain(data: DomainDKIMOption, params: RequestParams = {}) {
    return this.request({
      path: "/dkim",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getAccountIssues(params: RequestParams = {}) {
    return this.request<AccountIssuesView>({
      path: "/account/issues",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getAccountSubscriptionLead(params: RequestParams = {}) {
    return this.request<AccountLeadSubscriptionView>({
      path: "/account-subscription/lead",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getAccountSubscriptionFeatures(params: RequestParams = {}) {
    return this.request<SubscriptionFeaturesView>({
      path: "/account-subscription/features",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getAccountSubscriptionFeaturesCategories(params: RequestParams = {}) {
    return this.request<SubscriptionFeaturesCategoriesView>({
      path: "/account-subscription/features-categories",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getAccountIntegration(params: RequestParams = {}) {
    return this.request<AccountIntegrationsView>({
      path: "/account-integration",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getAccountIntegrationByIntegrationSystemName(
    integrationSystemName: string,
    params: RequestParams = {}
  ) {
    return this.request<AccountIntegrationItemView>({
      path: `/account-integration/${integrationSystemName}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getAccountIntegrationSummary(params: RequestParams = {}) {
    return this.request<AccountIntegrationsSummaryView>({
      path: "/account-integration/summary",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  deletePaymentAccountPaymentMethodById(
    id: string,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/payment-account/payment-method/${id}`,
      method: "DELETE",

      type: ContentType.Json,
      ...params,
    });
  }

  deleteCampaignActivitiesEmailMessageByCampaignIdAndEmailMessageId(
    campaignId: string,
    emailMessageId: string,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/campaign/${campaignId}`,
      method: "DELETE",

      type: ContentType.Json,
      ...params,
    });
  }

  deleteCampaignSegment(data: SegmentIdentityView, params: RequestParams = {}) {
    return this.request<void>({
      path: "/campaign/segment",
      method: "DELETE",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  deleteCampaignSegments(data: IdsView, params: RequestParams = {}) {
    return this.request<void>({
      path: "/campaign/segments",
      method: "DELETE",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  deleteAccountIntegrationById(id: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/account-integration/${id}`,
      method: "DELETE",

      type: ContentType.Json,
      ...params,
    });
  }

  getSegmentBySegmentId(segmentId: string, params: RequestParams = {}) {
    return this.request<SegmentDetailsView>({
      path: `/segment/${segmentId}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getSuppressionBySuppressionId(
    suppressionId: string,
    params: RequestParams = {}
  ) {
    return this.request<SuppressionView>({
      path: `/customer/suppression/suppression/${suppressionId}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  putSegmentBySegmentId(
    segmentId: string,
    data: SegmentUpdateView,
    params: RequestParams = {}
  ) {
    return this.request<SegmentIdentityView1>({
      path: `/segment/${segmentId}`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putSuppressionBySuppressionId(
    suppressionId: string,
    data: SegmentUpdateView,
    params: RequestParams = {}
  ) {
    return this.request<SuppressionView>({
      path: `/customer/suppression/suppression/${suppressionId}`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  deleteSegmentBySegmentId(segmentId: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/segment/${segmentId}`,
      method: "DELETE",

      type: ContentType.Json,
      ...params,
    });
  }

  deleteTrafficShapingId(segmentId: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/ai-traffic-control/${segmentId}`,
      method: "DELETE",

      type: ContentType.Json,
      ...params,
    });
  }

  resetTrafficShapingId(segmentId: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/ai-traffic-control/${segmentId}/reset`,
      method: "PUT",
      type: ContentType.Json,
      ...params,
    });
  }

  activateRecurringTrafficShapingId(
    aiTrafficControlId: string,
    activateRecurring: boolean,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/ai-traffic-control/${aiTrafficControlId}/recurring/${activateRecurring}`,
      method: "PUT",
      type: ContentType.Json,
      ...params,
    });
  }

  putSegmentCustomersBySegmentId(
    segmentId: string,
    data: CustomersUpdateView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/segment/${segmentId}/customers`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postSegmentCustomersBySegmentId(
    segmentId: string,
    data: CustomerBatchView,
    params: RequestParams = {}
  ) {
    return this.request<SegmentIdentityView1>({
      path: `/segment/${segmentId}/customers`,
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putSegmentStatisticByType(
    data: SegmentTypeStatisticRequestView,
    params: RequestParams = {}
  ) {
    return this.request<SegmentTypeStatisticResponseView>({
      path: "/segment/statistic/by-type",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putSegmentUniqueCountsById(
    data: SegmentUniqueCountRequestView,
    params: RequestParams = {}
  ) {
    return this.request<SegmentUniqueCountResponseView>({
      path: "/segment/unique-count",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putSegmentFindIds(data: IdsView, params: RequestParams = {}) {
    return this.request<SegmentIdentitiesView1>({
      path: "/segment/find/ids",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putSegmentFindFilter(
    data: SegmentFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
      countAllCustomers?: boolean;
    },
    params: RequestParams = {}
  ) {
    return this.request<SegmentPageSegmentView>({
      path: "/segment/find/filter",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getTrafficShapingItems(
    query: {
      page?: number;
      size?: number;
      sort?: string[];
      searchTextName?: string;
    },
    params: RequestParams = {}
  ) {
    return this.request<AITrafficShapingPageView>({
      path: "/ai-traffic-control",
      method: "GET",
      query: query,
      type: ContentType.Json,
      ...params,
    });
  }

  postTrafficShapingDetails(
    data: AITrafficCreateView,
    params: RequestParams = {}
  ) {
    return this.request<{
      id: string;
    }>({
      path: "/ai-traffic-control",
      method: "POST",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putSuppressionFindFilter(
    data: SuppressionFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<SuppressionPageSuppressionView>({
      path: "/customer/suppression/find/filter",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putSuppressionFindSearch(
    data: { email: string },
    params: RequestParams = {}
  ) {
    return this.request<SuppressionSearchResponseView>({
      path: "/customer/suppression/find/search",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putSegmentCustomSummary(
    data: CustomSegmentSummaryRequestView,
    params: RequestParams = {}
  ) {
    return this.request<CustomSegmentSummaryResponseView>({
      path: "/segment/custom/summary",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getSegmentCheckExists(
    query: {
      name: string;
    },
    params: RequestParams = {}
  ) {
    return this.request<boolean>({
      path: "/segment/check-exists",
      method: "GET",
      query: query,

      type: ContentType.Json,
      ...params,
    });
  }

  putSegmentCheckExists(
    data: SegmentCheckExistsFilterView,
    params: RequestParams = {}
  ) {
    return this.request<boolean>({
      path: "/segment/check-exists",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getSuppressionCheckExistsBySuppressionName(
    suppressionName: string,
    params: RequestParams = {}
  ) {
    return this.request<{ exist: boolean }>({
      path: `/customer/suppression/check-exists?name=${suppressionName}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  putCustomerByCustomerId(
    customerId: string,
    data: CustomerView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/customer/${customerId}`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putCustomerStatusByCustomerIdAndCustomerStatus(
    customerId: string,
    customerStatus: string,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/customer/${customerId}/status/${customerStatus}`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  putCustomerUpdateStatus(
    data: CustomerStatusUpdateView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: "/customer/update-status",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putCustomerFilter(
    data: CustomerFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageCustomerInfoView>({
      path: "/customer/filter",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putSuppressionFilter(
    data: SuppressionDetailsFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageSuppressionInfoView>({
      path: "/suppression/filter",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putCustomerFilterExport(
    data: CustomerFilterView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: "/customer/filter/export",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putSuppressionFilterExport(
    data: SuppressionDetailsFilterView,
    params: RequestParams = {}
  ) {
    return this.request<{ url: string }>({
      path: `/customer/suppression/${data?.suppressionId}/download`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  getSuppressionFiles(suppressionId: string, params: RequestParams = {}) {
    return this.request<SuppressionFilesView>({
      path: `/customer/suppression/${suppressionId}/download`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  getSuppressionFilesZip(suppressionId: string, params: RequestParams = {}) {
    return this.request<SuppressionZipView>({
      path: `/customer/suppression/${suppressionId}/download/zip`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  putCustomerCompositeFilter(
    data: CustomerCompositeFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageCustomerInfoView>({
      path: "/customer/composite-filter",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putCustomerActionPlatformInfoFindFilter(
    data: CustomerActionPlatformFindFilterView,
    params: RequestParams = {}
  ) {
    return this.request<CustomerActionPlatformInfoView[]>({
      path: "/customer-action/platform-info/find/filter",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putCustomerActionGeolocationInfoFindFilter(
    data: CustomerActionGeolocationFindFilterView,
    params: RequestParams = {}
  ) {
    return this.request<CustomerActionGeolocationInfoView[]>({
      path: "/customer-action/geolocation-info/find/filter",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getCustomerIspBreakdown(params: RequestParams = {}) {
    return this.request<IspBreakdownItemView[]>({
      path: "/customer/isp-breakdown",
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  putCustomerActionDomainInfoFindFilter(
    data: CustomerActionDomainFindFilterView,
    params: RequestParams = {}
  ) {
    return this.request<CustomerActionDomainInfoView[]>({
      path: "/customer-action/domain-info/find/filter",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putAccountSegmentCustomerFilterByAccountId(
    accountId: string,
    data: CustomerSegmentFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageCustomerSegmentInfoView>({
      path: `/account/${accountId}/segment-customer/filter`,
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putAccountCustomerFilterByAccountId(
    accountId: string,
    data: CustomerFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageCustomerInfoView>({
      path: `/account/${accountId}/customer/filter`,
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postSegmentCustomerBySegmentId(
    segmentId: string,
    data: CustomerView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/segment/${segmentId}/customer`,
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putMergeAudiences(data: AudienceMergeView, params: RequestParams = {}) {
    return this.request<SegmentView>({
      path: "/segment/merge",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putSegmentMove(data: SegmentMoveRequestView, params: RequestParams = {}) {
    return this.request<SegmentMoveResultView>({
      path: "/segment/move",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  deleteSegmentCustomerBySegmentId(
    segmentId: string,
    data: SegmentUnassignCustomersView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/segment/${segmentId}/customer`,
      method: "DELETE",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postSegmentUpload(data: UploadSegmentCreateView, params: RequestParams = {}) {
    return this.request<SegmentIdentityView1>({
      path: "/segment/upload",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postSegmentUploadCustomer(
    data: CustomerView,
    query: {
      segmentName: string;
    },
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: "/segment/upload/customer",
      method: "POST",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postSegmentStatisticsTotal(params: RequestParams = {}) {
    return this.request<SegmentTotalStatisticsResponseView>({
      path: "/segment/statistics/total",
      method: "POST",

      type: ContentType.Json,
      ...params,
    });
  }

  postSegmentStatisticsTotalCustomers(
    data: SegmentTotalCustomersStatisticsRequestView,
    params: RequestParams = {}
  ) {
    return this.request<SegmentTotalCustomersStatisticsResponseView>({
      path: "/segment/statistics/total-customers",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postSegmentIntegrationStatisticsGrowth(
    data: IntegrationSegmentStatisticsGrowthRequestView,
    params: RequestParams = {}
  ) {
    return this.request<IntegrationSegmentStatisticsGrowthResponseView>({
      path: "/segment/integration/statistics/growth",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postSegmentImport(data: SegmentImportView, params: RequestParams = {}) {
    return this.request<SegmentIdentityView1>({
      path: "/segment/import",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postSegmentCustom(data: CustomSegmentCreateView, params: RequestParams = {}) {
    return this.request<SegmentIdentityView1>({
      path: "/segment/custom",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postExportToStaticSegment(
    data: ExportSegmentCreateView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: "/segment/export/static-segment",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postSegmentClone(data: SegmentCloneRequestView, params: RequestParams = {}) {
    return this.request<SegmentIdentityView1>({
      path: "/segment/clone",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCustomerStatisticsTotal(
    data: CustomerStatisticsTotalRequestView,
    params: RequestParams = {}
  ) {
    return this.request<CustomerStatisticsTotalResponseView>({
      path: "/customer/statistics/total",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postSuppressionStatisticsTotal(
    data: SuppressionStatisticsTotalRequestView,
    params: RequestParams = {}
  ) {
    return this.request<SuppressionStatisticsTotalResponseView>({
      path: "/customer/suppression/statistics/total",
      method: "GET",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCustomerStatisticsGrouped(
    data: CustomerStatisticsGroupedRequestView,
    params: RequestParams = {}
  ) {
    return this.request<CustomerStatisticsGroupedResponseView>({
      path: "/customer/statistics/grouped",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCustomerImport(data: CustomerBatchView, params: RequestParams = {}) {
    return this.request<void>({
      path: "/customer/import",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCustomerEventStatisticsGrouped(
    data: CustomerEventStatisticsGroupedRequestView,
    params: RequestParams = {}
  ) {
    return this.request<CustomerEventStatisticsGroupedResponseView>({
      path: "/customer-event/statistics/grouped",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCustomerEventCampaignStatisticsTotalByCampaignId(
    campaignId: string,
    data: CustomerEventStatisticsTotalRequestView,
    params: RequestParams = {}
  ) {
    return this.request<CustomerEventStatisticsTotalResponseView>({
      path: `/customer-event/campaign/${campaignId}/statistics/total`,
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCustomerEventCampaignStatisticsMostOpenByCampaignId(
    campaignId: string,
    data: CustomerEventMostOpenStatisticsFilterView,
    query: {
      offset?: number;
      limit?: number;
    },
    params: RequestParams = {}
  ) {
    return this.request<CustomerEventMostOpenStatisticsResponseView>({
      path: `/customer-event/campaign/${campaignId}/statistics/most-open`,
      method: "POST",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCustomerEventCampaignStatisticsMostOpenExportByCampaignId(
    campaignId: string,
    data: CustomerEventMostOpenStatisticsFilterView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/customer-event/campaign/${campaignId}/statistics/most-open/export`,
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCustomerEventCampaignStatisticsMostClickByCampaignId(
    campaignId: string,
    data: CustomerEventMostClickStatisticsFilterView,
    query: {
      offset?: number;
      limit?: number;
    },
    params: RequestParams = {}
  ) {
    return this.request<CustomerEventMostClickStatisticsResponseView>({
      path: `/customer-event/campaign/${campaignId}/statistics/most-click`,
      method: "POST",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCustomerEventCampaignStatisticsMostClickExportByCampaignId(
    campaignId: string,
    data: CustomerEventMostClickStatisticsFilterView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/customer-event/campaign/${campaignId}/statistics/most-click/export`,
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCustomerEventCampaignStatisticsGroupedByCampaignId(
    campaignId: string,
    data: CustomerEventStatisticsGroupedRequestView,
    params: RequestParams = {}
  ) {
    return this.request<CustomerEventStatisticsGroupedResponseView>({
      path: `/customer-event/campaign/${campaignId}/statistics/grouped`,
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCustomerEventCampaignStatisticsExportByCampaignId(
    campaignId: string,
    params: RequestParams = {},
    body?: any
  ) {
    return this.request<void>({
      path: `/customer-event/campaign/${campaignId}/statistics/export`,
      method: "POST",
      body: body,
      type: ContentType.Json,
      ...params,
    });
  }

  postAccountSegmentCustomersByAccountIdAndSegmentId(
    accountId: string,
    segmentId: string,
    data: CustomerBatchView,
    params: RequestParams = {}
  ) {
    return this.request<SegmentIdentityView1>({
      path: `/account/${accountId}/segment/${segmentId}/customers`,
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postAccountSegmentImportByAccountId(
    accountId: string,
    data: SegmentImportView,
    params: RequestParams = {}
  ) {
    return this.request<SegmentIdentityView1>({
      path: `/account/${accountId}/segment/import`,
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getSegmentCustomCustomers(
    query: {
      segmentId: string;
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageCustomerInfoView>({
      path: "/segment/custom/customers",
      method: "GET",
      query: query,

      type: ContentType.Json,
      ...params,
    });
  }

  getSegmentConditionsInfo(params: RequestParams = {}) {
    return this.request<SegmentAttributesView>({
      path: "/segment/conditions/info",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getSegmentCheckExistsBySegmentName(
    segmentName: string,
    params: RequestParams = {}
  ) {
    return this.request<boolean>({
      path: `/segment/check-exists/${segmentName}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getSegmentBySegmentType(
    query: {
      searchSegmentType:
        | "UPLOAD"
        | "INTEGRATION"
        | "CUSTOM"
        | "PREDEFINED_RECOMMENDED"
        | "PREDEFINED_LIBRARY";
    },
    params: RequestParams = {}
  ) {
    return this.request<SegmentsView>({
      path: "/segment/by-segment-type",
      method: "GET",
      query: query,

      type: ContentType.Json,
      ...params,
    });
  }

  getSegmentByName(
    query: {
      searchTextName: string;
    },
    params: RequestParams = {}
  ) {
    return this.request<SegmentIdentitiesView1>({
      path: "/segment/by-name",
      method: "GET",
      query: query,

      type: ContentType.Json,
      ...params,
    });
  }

  getCustomerTags(params: RequestParams = {}) {
    return this.request<TagsView>({
      path: "/customer/tags",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getAccountSegmentByAccountId(
    accountId: string,
    query: {
      name: string;
    },
    params: RequestParams = {}
  ) {
    return this.request<SegmentView>({
      path: `/account/${accountId}/segment`,
      method: "GET",
      query: query,

      type: ContentType.Json,
      ...params,
    });
  }

  getAccountCustomerByAccountId(
    accountId: string,
    query: {
      email: string;
    },
    params: RequestParams = {}
  ) {
    return this.request<CustomerInfoView>({
      path: `/account/${accountId}/customer`,
      method: "GET",
      query: query,

      type: ContentType.Json,
      ...params,
    });
  }

  getAccountCustomerTotalByAccountId(
    accountId: string,
    query: {
      segmentIds: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<CustomerTotalView>({
      path: `/account/${accountId}/customer/total`,
      method: "GET",
      query: query,

      type: ContentType.Json,
      ...params,
    });
  }

  deleteSegment(data: SegmentMultipleDeleteView, params: RequestParams = {}) {
    return this.request<void>({
      path: "/segment",
      method: "DELETE",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  deleteSuppression(
    data: SuppressionMultipleDeleteView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: "/customer/suppression",
      method: "DELETE",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  deleteSegmentCustomerBySegmentIdAndCustomerId(
    segmentId: string,
    customerId: string,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/segment/${segmentId}/customer/${customerId}`,
      method: "DELETE",

      type: ContentType.Json,
      ...params,
    });
  }

  deleteCustomer(data: IdsView, params: RequestParams = {}) {
    return this.request<void>({
      path: "/customer",
      method: "DELETE",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  deleteSuppressionDetailsItem(
    suppressionId: string,
    data: IdsView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/customer/suppression/${suppressionId}/suppression_value`,
      method: "DELETE",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  deleteCustomerById(id: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/customer/${id}`,
      method: "DELETE",

      type: ContentType.Json,
      ...params,
    });
  }

  putContentSubjectFilter(
    data: SubjectLineFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageSubjectLineView>({
      path: "/content/subject/filter",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putContentSubjectFavoriteByIdAndIsFavorite(
    id: string,
    isFavorite: boolean,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/content/subject/${id}/favorite/${isFavorite}`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  putToggleSuppressionType(
    suppressionId: string,
    scope: "GLOBAL" | "LOCAL",
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/customer/suppression/${suppressionId}/change-scope/${scope}`,
      method: "PUT",
      type: ContentType.Json,
      ...params,
    });
  }

  postContentSubjectGenerate(
    data: SubjectGenerateRequestView,
    params: RequestParams = {}
  ) {
    return this.request<SubjectGenerateResponseView>({
      path: "/content/subject/generate",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getTone(params: RequestParams = {}) {
    return this.request<TonesView>({
      path: "/tone",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getMergeTag(params: RequestParams = {}) {
    return this.request<MergeTagsView>({
      path: "/merge-tag",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getContentSubjectFeature(params: RequestParams = {}) {
    return this.request<SubjectLineFeatureInfoView>({
      path: "/content/subject/feature",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  putFormById(
    id: string,
    data: FormUpdateRequestView,
    params: RequestParams = {}
  ) {
    return this.request<FormUpdateResponseView>({
      path: `/form/${id}`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putFormStatusByIdAndFormStatus(
    id: string,
    formStatus: string,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/form/${id}/status/${formStatus}`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  putFormFilter(
    data: FormFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageFormInfoView>({
      path: "/form/filter",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putFormConfig(data: FormConfigView, params: RequestParams = {}) {
    return this.request<void>({
      path: "/form/config/",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putFormConfigContentById(
    id: string,
    data: FormContentView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/form/config/${id}/content`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putFormConfigAppearanceById(
    id: string,
    data: FormAppearanceView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/form/config/${id}/appearance`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putFormConfigAllowedDomainsById(
    id: string,
    data: FormAllowedDomainsView,
    params: RequestParams = {}
  ) {
    return this.request<FormAllowedDomainsView>({
      path: `/form/config/${id}/allowed-domains`,
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putFormCheckAssignedAudiences(
    data: FormCheckAssignedAudiencesRequestView,
    params: RequestParams = {}
  ) {
    return this.request<FormCheckAssignedAudiencesResponseView>({
      path: "/form/check/assigned-audiences",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putFormTemplateFavoriteByIdAndIsFavorite(
    id: string,
    isFavorite: boolean,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/form-template/${id}/favorite/${isFavorite}`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  putFormTemplateFilter(
    data: FormTemplateFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageFormTemplateInfoView>({
      path: "/form-template/filter",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postForm(data: FormView, params: RequestParams = {}) {
    return this.request<FormIdentityView>({
      path: "/form",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  deleteForm(data: FormDeleteRequestView, params: RequestParams = {}) {
    return this.request<void>({
      path: "/form",
      method: "DELETE",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postFormClone(data: FormCloneRequestView, params: RequestParams = {}) {
    return this.request<FormCloneResponseView>({
      path: "/form/clone",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postFormByTemplate(data: FormWithTemplateView, params: RequestParams = {}) {
    return this.request<FormIdentityView>({
      path: "/form/by-template",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getFormProviderByAccountIdAndFormId(
    accountId: string,
    formId: string,
    params: RequestParams = {}
  ) {
    return this.request<FormPayloadView>({
      path: `/form-provider/${accountId}/${formId}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  postFormProviderByAccountIdAndFormId(
    accountId: string,
    formId: string,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/form-provider/${accountId}/${formId}`,
      method: "POST",

      type: ContentType.Json,
      ...params,
    });
  }

  getFormConfigById(id: string, params: RequestParams = {}) {
    return this.request<FormDetailsView>({
      path: `/form/config/${id}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getFormTemplateTags(params: RequestParams = {}) {
    return this.request<TemplateTagsView>({
      path: "/form-template/tags",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  putAutomationEnabledByAutomationIdAndIsEnabled(
    automationId: string,
    isEnabled: boolean,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/automation/${automationId}/enabled/${isEnabled}`,
      method: "PUT",

      type: ContentType.Json,
      ...params,
    });
  }

  getAutomation(params: RequestParams = {}) {
    return this.request<AutomationsView>({
      path: "/automation",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getEmailAuthenticationStatusByEmail(
    email: string,
    params: RequestParams = {}
  ) {
    return this.request<SenderDetailsAuthenticationView>({
      path: `/email-auth/validate?email=${email}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }
  getEntriToken(applicationId: string, params: RequestParams = {}) {
    return this.request<EntriTokenView>({
      path: "/entri/token",
      method: "GET",
      query: { applicationId },
      type: ContentType.Json,
      ...params,
    });
  }

  getCnameRecords(email: string, params: RequestParams = {}) {
    return this.request<DnsRecord[]>({
      path: `/email-auth/dns-records?email=${email}`,
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  resolveAccountIssue(data: ResolveIssueView, params: RequestParams = {}) {
    return this.request<void>({
      path: "/account/issue/resolve",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putDomainLevelMetrics(
    data: DomainLevelMetricsFilter,
    params: RequestParams = {}
  ) {
    return this.request<DomainLevelMetricsResponse>({
      path: "/analytics/domain-level-metrics",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postDomainLevelMetricsExport(
    data: DomainLevelMetricsFilter,
    params: RequestParams = {}
  ) {
    return this.request<DomainLevelMetricsResponse>({
      path: "/analytics/domain-level-metrics/export",
      method: "POST",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postAccountMemberResendInvitation(id: string, params: RequestParams = {}) {
    return this.request<void>({
      path: `/account-member/re-send/invitation/${id}`,
      method: "POST",
      type: ContentType.Json,
      ...params,
    });
  }

  getAccountMemberMePermissions(params: RequestParams = {}) {
    return this.request<AccountMemberPermissionsView>({
      path: "/account-member/me/permissions",
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  getAccountMemberAudit(
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageAccountMemberAuditView>({
      path: "/account-member/audit",
      method: "GET",
      query: query,
      type: ContentType.Json,
      ...params,
    });
  }

  postAccountMember(data: AccountMemberCreateView, params: RequestParams = {}) {
    return this.request<UserDetailsView>({
      path: "/account-member",
      method: "POST",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putAccountMember(data: AccountMemberUpdateView, params: RequestParams = {}) {
    return this.request<UserDetailsView>({
      path: "/account-member",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putDomainLevelMetricsAiInsights(
    data: AIInsightsFilter,
    params: RequestParams = {}
  ) {
    return this.request<{ insights: string }>({
      path: "/analytics/domain-level-metrics/ai-insights",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postRelayStats(
    data: RelayStatsFilter,
    query: { page: number; size: number; sort: string },
    params: RequestParams = {}
  ) {
    return this.request<RelayStatsResponse>({
      path: "/dashboard/relay-stats",
      method: "POST",
      body: data,
      query,
      type: ContentType.Json,
      ...params,
    });
  }

  createMemberPassword(data: SetupMemberPassword, params: RequestParams = {}) {
    return this.request<void>({
      path: "/account-member/create/password",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putCampaignLevelMetricsAiInsights(
    data: CampaignFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<{ insights: string }>({
      path: "/campaign-analytics/campaign-level-metrics/ai-insights",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      query: query,
      ...params,
    });
  }

  getEmailContentCheck(campaignId: string, params: RequestParams = {}) {
    return this.request<EmailContentCheckResponse>({
      path: `/campaign/${campaignId}/link-analysis`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  postDomainStatistics(
    data: { activityId: string; accountId: string; domainName?: string },
    query: { page?: number; size?: number; sort?: string[] },
    params: RequestParams = {}
  ) {
    return this.request<any>({
      path: `/customer-action/account/${data.accountId}/domain-wise-statistics`,
      method: "POST",
      query: query,
      body: { activityId: data.activityId, domainName: data.domainName },
      type: ContentType.Json,
      ...params,
    });
  }

  getEmailUnvettedCheck(campaignId: string, params: RequestParams = {}) {
    return this.request<EmailUnvettedCheckResponse>({
      path: `/campaign/${campaignId}/unvetted-restriction`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  putWorkflowFindFilter(
    data: WorkflowFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<PageWorkflowInfoView>({
      path: "/workflow",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putCheckCustomSegmentExists(
    data: SegmentDataExistCheckView,
    params: RequestParams = {}
  ) {
    return this.request<{ dataAvailable: boolean }>({
      path: "/ai-traffic-control/custom-segment/exists",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putAITrafficControlLogs(
    data: AITrafficControlLogsFilter,
    page = 0,
    size = 5,
    sort = "createdAt,DESC",
    params: RequestParams = {}
  ) {
    return this.request<AITrafficControlLogsResponse>({
      path: `/ai-traffic-control/history/logs?page=${page}&size=${size}&sort=${sort}`,
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putAITrafficControlStats(
    data: AITrafficControlStatsFilter,
    params: RequestParams = {}
  ) {
    return this.request<AITrafficControlStatsResponse>({
      path: "/ai-traffic-control/history/stats",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putAITrafficControlEngagerStats(
    data: AITrafficControlEngagerStatsFilter,
    params: RequestParams = {}
  ) {
    return this.request<AITrafficControlEngagerStatsResponse>({
      path: "/ai-traffic-control/history/engager/stats",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putAITrafficControlSegments(
    data: AITrafficControlSegmentsFilter,
    params: RequestParams = {}
  ) {
    return this.request<AITrafficControlSegmentsResponse>({
      path: "/ai-traffic-control/segments",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putAITrafficControlProgress(
    data: AITrafficControlSegmentProgressRequest,
    params: RequestParams = {}
  ) {
    return this.request<AITrafficControlSegmentProgressResponse>({
      path: "/ai-traffic-control/progress",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getSubAccounts(params: RequestParams = {}) {
    return this.request<SubAccountUsageResponseView>({
      path: "/account/master/accounts-usages",
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  getAccountDeliverability(params: RequestParams = {}) {
    return this.request<DeliverabilitySettingsView>({
      path: "/account/deliverability",
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  putAccountDeliverability(
    data: Partial<DeliverabilitySettingsView>,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: "/account/deliverability",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getGlobalActiveEngagerSegment(accountId: string, params: RequestParams = {}) {
    return this.request<GlobalActiveEngagerSegmentView>({
      path: `/global-active-engager/${accountId}/segment`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  getGlobalActiveEngagerSettings(
    accountId: string,
    params: RequestParams = {}
  ) {
    return this.request<GlobalActiveEngagerSettingsView>({
      path: `/global-active-engager/${accountId}/settings`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  putGlobalActiveEngagerSettings(
    accountId: string,
    data: GlobalActiveEngagerSettingsView,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/global-active-engager/${accountId}/settings`,
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putPreSignedUrlGenerate(
    data: PreSignedUrlGenerateRequestView,
    params: RequestParams = {}
  ) {
    return this.request<PreSignedUrlGenerateResponseView>({
      path: "/pre-signed-url/generate",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getCreditBalance(productId: string) {
    return this.request<CreditBalanceView>({
      path: `/credits/product/${productId}/balance`,
      method: "GET",
      type: ContentType.Json,
    });
  }

  postCreditPurchaseByPaymentMethodId(
    paymentMethodId: string,
    data: CreditPurchaseRequestView
  ) {
    return this.request<CreditPurchaseResponseView>({
      path: `/credits/purchase/${paymentMethodId}`,
      method: "POST",
      body: data,
      type: ContentType.Json,
    });
  }

  postCreditsUse(data: CreditsUseRequestView) {
    return this.request<CreditsUseResponseView>({
      path: "/credits/use",
      method: "POST",
      body: data,
      type: ContentType.Json,
    });
  }

  postCreditsCalculate(data: CreditsCalculateRequestView) {
    return this.request<CreditsCalculateResponseView>({
      path: "/credits/calculate-cost",
      method: "POST",
      body: data,
      type: ContentType.Json,
    });
  }

  getProductUsageHistory(
    productId: string,
    params: { page?: number; size?: number } = {}
  ) {
    return this.request<ProductUsageHistoryView>({
      path: `/product/${productId}/usage-history`,
      method: "GET",
      query: params,
      type: ContentType.Json,
    });
  }

  getProductPurchaseHistory(
    productId: string,
    params: { page?: number; size?: number } = {}
  ) {
    return this.request<ProductPurchaseHistoryView>({
      path: `/product/${productId}/purchase-history`,
      method: "GET",
      query: params,
      type: ContentType.Json,
    });
  }

  getAllBillingProducts() {
    return this.request<ProductView[]>({
      path: "/product",
      method: "GET",
      type: ContentType.Json,
    });
  }

  getRetentionIntegration() {
    return this.request<RetentionIntegrationView>({
      path: "/product/retention/integration",
      method: "GET",
      type: ContentType.Json,
    });
  }

  postRetentionActivate(data: {
    segmentId: string;
    reactivationSegmentId?: string;
    website?: string;
  }) {
    return this.request<RetentionIntegrationView>({
      path: "/product/retention/activate",
      method: "POST",
      body: data,
      type: ContentType.Json,
    });
  }

  getAutoTopUpConfig(productId: string) {
    return this.request<AutoTopUpConfigView>({
      path: `/credits/product/${productId}/auto-top-up`,
      method: "GET",
      type: ContentType.Json,
    });
  }

  putAutoTopUpConfig(productId: string, data: AutoTopUpConfigUpdateView) {
    return this.request<AutoTopUpConfigView>({
      path: `/credits/product/${productId}/auto-top-up`,
      method: "PUT",
      body: data,
      type: ContentType.Json,
    });
  }

  postFormImage(data: { image: File }, params: RequestParams = {}) {
    return this.request<FormImageUploadResponseView>({
      path: "/forms/images",
      method: "POST",
      body: data,
      type: ContentType.FormData,
      ...params,
    });
  }

  postNewSubscriberEngagement(data: SubscriberEngagementRequestView) {
    return this.request<SubscriberEngagementItemView[]>({
      path: "/analytics/audience/new-subscriber-engagement",
      method: "POST",
      body: data,
      type: ContentType.Json,
    });
  }

  postIspNewSubscriberEngagement(data: IspEngagementRequestView) {
    return this.request<{ targetedStats: IspEngagementItemView[] }>({
      path: "/analytics/audience/isp/new-subscriber-engagement",
      method: "POST",
      body: data,
      type: ContentType.Json,
    });
  }

  getCustomAttributes() {
    return this.request<CustomAttributesResponseView>({
      path: "/custom-attributes/get",
      method: "GET",
      type: ContentType.Json,
    });
  }

  createCustomAttribute(data: CreateCustomAttributeRequestView) {
    return this.request<CustomAttributeDefinitionView>({
      path: "/custom-attributes/create",
      method: "POST",
      body: data,
      type: ContentType.Json,
    });
  }

  deleteCustomAttribute(data: DeleteCustomAttributeRequestView) {
    return this.request<void>({
      path: "/custom-attributes/delete",
      method: "DELETE",
      body: data,
      type: ContentType.Json,
    });
  }

  getCustomAttributeMergeTags() {
    return this.request<CustomAttributeMergeTagsView>({
      path: "/custom-attributes/merge-tag",
      method: "GET",
      type: ContentType.Json,
    });
  }

  putWorkflowEmailTemplatePreview(data: WorkflowTemplatePreviewFilterView) {
    return this.request<WorkflowTemplatePreviewResponseView>({
      path: "/workflow-email-template/preview",
      method: "PUT",
      body: data,
      type: ContentType.Json,
    });
  }

  postEngagementScoringAnalytics(
    data: EngagementScoringAnalyticsRequest,
    params: RequestParams = {}
  ) {
    return this.request<EngagementScoringAnalyticsResponse>({
      path: "/engagement-scoring/analytics",
      method: "POST",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getRelayManagement(
    query: { page: number; size: number; sort?: string; name?: string },
    params: RequestParams = {}
  ) {
    return this.request<RelayManagementPageResponse>({
      path: "/relay-management",
      method: "GET",
      query,
      type: ContentType.Json,
      ...params,
    });
  }

  getRelayManagementApiKey(relayId: string, params: RequestParams = {}) {
    return this.request<RelayApiKeyResponse>({
      path: `/relay-management/${relayId}/api-key`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  postApiKeys(data: ApiKeyCreateRequestView, params: RequestParams = {}) {
    return this.request<ApiKeyView>({
      path: "/api-keys",
      method: "POST",
      body: data,
      type: ContentType.Json,
      format: "json",
      ...params,
    });
  }

  getApiKeys(params: RequestParams = {}) {
    return this.request<ApiKeyView[]>({
      path: "/api-keys",
      method: "GET",
      format: "json",
      ...params,
    });
  }

  putApiKeys(data: ApiKeyUpdateRequestView, params: RequestParams = {}) {
    return this.request<ApiKeyView>({
      path: "/api-keys",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      format: "json",
      ...params,
    });
  }

  deleteApiKeysById(id: number, params: RequestParams = {}) {
    return this.request<void>({
      path: `/api-keys/${id}`,
      method: "DELETE",
      ...params,
    });
  }

  getCampaignFeedbackSummary(
    query: { dateFrom?: string; dateTo?: string } = {},
    params: RequestParams = {}
  ) {
    return this.request<FeedbackSummaryResponseView>({
      path: "reportservice/campaign/feedback/summary",
      method: "GET",
      query,
      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignFeedbackRankings(
    query: CampaignFeedbackRankingQuery = {},
    params: RequestParams = {}
  ) {
    return this.request<CampaignFeedbackRankingResponseView>({
      path: "reportservice/campaign/feedback/rankings",
      method: "GET",
      query,
      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignFeedbackSummaryById(id: string, params: RequestParams = {}) {
    return this.request<FeedbackSummaryResponseView>({
      path: `reportservice/campaign/feedback/${id}/summary`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignFeedbackDistributionById(id: string, params: RequestParams = {}) {
    return this.request<FeedbackDistributionResponseView>({
      path: `reportservice/campaign/feedback/${id}/distribution`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }

  getCampaignFeedbackTimeSeriesById(id: string, params: RequestParams = {}) {
    return this.request<FeedbackTimeSeriesResponseView>({
      path: `reportservice/campaign/feedback/${id}/time-series`,
      method: "GET",
      type: ContentType.Json,
      ...params,
    });
  }
}

export interface FeedbackSummaryResponseView {
  avgRating: number;
  responses: number;
  responseRate: number;
}

export interface CampaignFeedbackRankingItemView {
  campaignId: string;
  campaignName: string;
  sentDate: string;
  avgRating: number;
  responses: number;
  responseRate: number;
  distribution: Record<string, number>;
}

export interface CampaignFeedbackRankingResponseView {
  content: CampaignFeedbackRankingItemView[];
  totalElements: number;
  page: number;
  size: number;
}

export interface FeedbackDistributionRatingView {
  rating: number;
  count: number;
  percent: number;
}

export interface FeedbackDistributionResponseView {
  total: number;
  ratings: FeedbackDistributionRatingView[];
}

export interface FeedbackTimeSeriesPointView {
  date: string;
  avgRating: number;
}

export interface FeedbackTimeSeriesResponseView {
  points: FeedbackTimeSeriesPointView[];
}

export type CampaignFeedbackRankingSort =
  | "HIGHEST_RATED"
  | "LOWEST_RATED"
  | "MOST_RESPONSES"
  | "FEWEST_RESPONSES";

export interface CampaignFeedbackRankingQuery {
  dateFrom?: string;
  dateTo?: string;
  sort?: CampaignFeedbackRankingSort;
  search?: string;
  page?: number;
  size?: number;
}
